import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Box, 
  Flex, 
  Heading, 
  Text, 
  SimpleGrid, 
  Stat, 
  Spinner,
  Card,
  Button,
  Input,
  NativeSelect,
  HStack,
  VStack,
  Badge,
  Icon,
  IconButton,
  Field,
  NumberInput,
  useDisclosure,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerBackdrop,
  DrawerContent,
  DrawerCloseTrigger,
  Table,
} from '@chakra-ui/react';
import {
  DialogRoot,
  DialogBackdrop,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogBody,
  DialogCloseTrigger,
} from '../components/ui/dialog';
import { useColorModeValue } from '../hooks/useColorModeValue';
import { 
  FiTrash2, 
  FiUsers, 
  FiShield, 
  FiActivity,
  FiDollarSign,
  FiCpu,
  FiDatabase,
  FiUpload,
  FiEdit,
  FiEye,
  FiKey,
  FiSettings,
  FiDownload,
  FiBookOpen,
  FiLifeBuoy,
  FiCloud,
  FiCreditCard,
  FiPlus,
  FiMenu,
  FiRefreshCw,
  FiAlertTriangle,
  FiExternalLink,
  FiMessageCircle,
} from 'react-icons/fi';
import AdminSidebar from '../components/AdminSidebar';
import { useAdminDashboardStats } from '../hooks/useApi';
import { useSystemStatus } from '../hooks/useSystemStatus';
import MultiStandardUpload from '../components/MultiStandardUpload';
import { UserManagement } from '../components/UserManagement';
import ContentReviewsManagement from '../components/ContentReviewsManagement';
import AnalyticsReporting from '../components/AnalyticsReporting';
import { adminAPI } from '../services/api';
import { 
  useSubscriptionPlans, 
  useDeleteSubscriptionPlan,
  useCreateSubscriptionPlan,
  useUpdateSubscriptionPlan,
  useUsageOverview, 
  useUsers,
  useRevenueAnalytics, 
  usePerformanceMetrics,
  useAppSettings,
  useIntegrations,
  useEnvironmentInfo
} from '../hooks/useApi';
import SubscriptionManagement from '../components/SubscriptionManagement';
import SecurityComplianceSection from '../components/SecurityComplianceSection';
import { toaster } from '../components/ui/toaster';

// Enhanced AdminDashboard component
const AdminDashboard = ({ setSection }: { setSection: (section: string) => void }) => {
  const { t } = useTranslation();
  const { data: stats, isLoading: statsLoading } = useAdminDashboardStats();
  const { isLoading: usersLoading } = useUsers();
  const { data: systemStatus, isLoading: statusLoading } = useSystemStatus();
  const { isLoading: usageLoading } = useUsageOverview();
  const { data: performanceMetrics, isLoading: performanceLoading } = usePerformanceMetrics();
  const { data: revenueAnalytics, isLoading: revenueLoading } = useRevenueAnalytics('30d');
  
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const displayStatus = (status?: string) => status ? t(`admin.main.status.${status}`, { defaultValue: status }) : t('admin.main.status.unknown');

  if (statsLoading || usersLoading || statusLoading || usageLoading || performanceLoading || revenueLoading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="xl" />
        <Text mt={4}>{t('admin.main.dashboard.loading')}</Text>
      </Box>
    );
  }

  const dashboardMetrics = [
    {
      label: t('admin.main.dashboard.metrics.totalUsers'),
      value: stats?.user_count || 0,
      icon: FiUsers,
      color: 'blue',
      description: t('admin.main.dashboard.metrics.activeRegisteredUsers')
    },
    {
      label: t('admin.main.dashboard.metrics.standardsLibrary'),
      value: stats?.standard_count || 0,
      icon: FiShield,
      color: 'purple',
      description: t('admin.main.dashboard.metrics.availableComplianceStandards')
    },
    {
      label: t('admin.main.dashboard.metrics.totalAssessments'),
      value: stats?.review_count || 0,
      icon: FiActivity,
      color: 'orange',
      description: t('admin.main.dashboard.metrics.completedComplianceAssessments')
    },
    {
      label: t('admin.main.dashboard.metrics.monthlyRevenue'),
      value: `$${revenueAnalytics?.total_revenue || 0}`,
      icon: FiDollarSign,
      color: 'green',
      description: t('admin.main.dashboard.metrics.currentMonthRevenue')
    }
  ];

  const recentActivity: any[] = [];

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'user': return FiUsers;
      case 'review': return FiActivity;
      case 'standard': return FiShield;
      case 'billing': return FiDollarSign;
      default: return FiActivity;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'user': return 'blue';
      case 'review': return 'green';
      case 'standard': return 'purple';
      case 'billing': return 'orange';
      default: return 'gray';
    }
  };

  // Quick Actions handlers
  const handleQuickAction = (action: string) => {
    switch (action) {
      case 'add_user':
        setSection('users');
        break;
      case 'upload_standard':
        setSection('standards');
        break;
      case 'view_reviews':
        setSection('content');
        break;
      case 'billing':
        setSection('billing');
        break;
      default:
        break;
    }
  };

  return (
    <Box p={6}>
      <VStack gap={6} align="stretch">
        <Box>
          <Heading size="lg" mb={2}>{t('admin.main.dashboard.title')}</Heading>
          <Text color="gray.600" fontSize="sm">
            {t('admin.main.dashboard.welcome')}
          </Text>
        </Box>
        
        {/* Stats Cards */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={6}>
          {dashboardMetrics.map((metric, index) => (
            <Card.Root key={index} bg={cardBg} border="1px" borderColor={borderColor} _hover={{ shadow: 'md' }} transition="all 0.2s">
              <Card.Body>
                <HStack justify="space-between" mb={3}>
                  <Icon as={metric.icon} color={`${metric.color}.500`} boxSize={6} />
                </HStack>
                <Stat.Root>
                  <Stat.Label color="gray.600" fontSize="sm" mb={1}>
                    {metric.label}
                  </Stat.Label>
                  <Stat.ValueText fontSize="2xl" fontWeight="bold" color={`${metric.color}.500`} mb={1}>
                    {metric.value}
                  </Stat.ValueText>
                  <Stat.HelpText mb={0} fontSize="xs">
                    {metric.description}
                  </Stat.HelpText>
                </Stat.Root>
              </Card.Body>
            </Card.Root>
          ))}
        </SimpleGrid>

        <SimpleGrid columns={{ base: 1, lg: 2 }} gap={6}>
          {/* System Status */}
          <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
            <Card.Header>
              <Heading size="md">{t('admin.main.systemHealth.title')}</Heading>
            </Card.Header>
            <Card.Body>
              <VStack gap={4} align="stretch">
                <HStack justify="space-between">
                  <HStack gap={3}>
                    <Icon as={FiDatabase} color="blue.500" />
                    <Text>{t('admin.main.dashboard.systemStatus.databaseStatus')}</Text>
                  </HStack>
                  <Badge colorScheme={systemStatus?.database_status === 'healthy' ? 'green' : 'red'}>
                    {displayStatus(systemStatus?.database_status)}
                  </Badge>
                </HStack>
                <HStack justify="space-between">
                  <HStack gap={3}>
                    <Icon as={FiCpu} color="purple.500" />
                    <Text>{t('admin.main.systemHealth.services.aiService')}</Text>
                  </HStack>
                  <Badge colorScheme={systemStatus?.ai_model_status === 'available' ? 'green' : 'red'}>
                    {displayStatus(systemStatus?.ai_model_status)}
                  </Badge>
                </HStack>
                <HStack justify="space-between">
                  <HStack gap={3}>
                    <Icon as={FiCloud} color="orange.500" />
                    <Text>{t('admin.main.systemHealth.services.vectorDatabase')}</Text>
                  </HStack>
                  <Badge colorScheme={systemStatus?.embedding_service_status === 'operational' ? 'green' : 'red'}>
                    {displayStatus(systemStatus?.embedding_service_status)}
                  </Badge>
                </HStack>
                <HStack justify="space-between">
                  <HStack gap={3}>
                    <Icon as={FiActivity} color="green.500" />
                    <Text>{t('admin.main.dashboard.systemStatus.redisCache')}</Text>
                  </HStack>
                  <Badge colorScheme={systemStatus?.redis_status === 'connected' ? 'green' : 'red'}>
                    {displayStatus(systemStatus?.redis_status)}
                  </Badge>
                </HStack>
                <HStack justify="space-between">
                  <HStack gap={3}>
                    <Icon as={FiCloud} color="cyan.500" />
                    <Text>{t('admin.main.systemHealth.services.objectStorage')}</Text>
                  </HStack>
                  <VStack gap={0} align="end">
                    <Badge colorScheme={systemStatus?.storage_status?.status === 'healthy' ? 'green' : 'red'}>
                      {displayStatus(systemStatus?.storage_status?.status)}
                    </Badge>
                    <Text fontSize="xs" color="gray.500">
                      {systemStatus?.storage_status?.backend === 's3' ? 'S3 / MinIO' : t('admin.main.systemHealth.localFilesystem')}
                    </Text>
                  </VStack>
                </HStack>
              </VStack>
            </Card.Body>
          </Card.Root>

          {/* Recent Activity */}
          <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
            <Card.Header>
              <Heading size="md">{t('admin.main.dashboard.recentActivity')}</Heading>
            </Card.Header>
            <Card.Body>
              {recentActivity.length > 0 ? (
                <VStack gap={3} align="stretch">
                  {recentActivity.map((activity, index) => (
                    <HStack key={index} justify="space-between" p={2} bg="gray.50" borderRadius="md">
                      <HStack gap={3}>
                        <Icon as={getActivityIcon(activity.type)} color={`${getActivityColor(activity.type)}.500`} boxSize={4} />
                        <VStack align="start" gap={0}>
                          <Text fontSize="sm" fontWeight="medium">{activity.action}</Text>
                          <Text fontSize="xs" color="gray.600">{activity.user}</Text>
                        </VStack>
                      </HStack>
                      <Text fontSize="xs" color="gray.500">{activity.time}</Text>
                    </HStack>
                  ))}
                </VStack>
              ) : (
                <Text color="gray.500" textAlign="center" py={4}>
                  {t('admin.main.dashboard.noRecentActivity')}
                </Text>
              )}
            </Card.Body>
          </Card.Root>
        </SimpleGrid>

        {/* Quick Actions */}
        <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
          <Card.Header>
            <Heading size="md">{t('admin.main.dashboard.quickActions')}</Heading>
          </Card.Header>
          <Card.Body>
            <SimpleGrid columns={{ base: 2, md: 4 }} gap={4}>
              <Button 
                colorScheme="blue" 
                size="sm"
                onClick={() => handleQuickAction('add_user')}
              >
                <FiUsers />
                {t('admin.main.dashboard.quickActionsAddUser')}
              </Button>
              <Button 
                colorScheme="purple" 
                size="sm"
                onClick={() => handleQuickAction('upload_standard')}
              >
                <FiShield />
                {t('admin.main.dashboard.quickActionsUploadStandard')}
              </Button>
              <Button 
                colorScheme="green" 
                size="sm"
                onClick={() => handleQuickAction('view_reviews')}
              >
                <FiActivity />
                {t('admin.main.dashboard.quickActionsViewAssessments')}
              </Button>
              <Button 
                colorScheme="orange" 
                size="sm"
                onClick={() => handleQuickAction('billing')}
              >
                <FiDollarSign />
                {t('admin.main.sidebar.items.billing.label')}
              </Button>
            </SimpleGrid>
          </Card.Body>
        </Card.Root>

        {/* Performance Metrics */}
        <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
          <Card.Header>
            <Heading size="md">{t('admin.main.dashboard.performanceOverview')}</Heading>
          </Card.Header>
          <Card.Body>
            <SimpleGrid columns={{ base: 1, md: 4 }} gap={6}>
              <Stat.Root>
                <Stat.Label>{t('admin.main.dashboard.performance.apiResponseTime')}</Stat.Label>
                <Stat.ValueText fontSize="lg">{performanceMetrics?.api_response_time || 0}ms</Stat.ValueText>
                <Stat.HelpText>
                  {t('admin.main.dashboard.performance.averageResponseTime')}
                </Stat.HelpText>
              </Stat.Root>
              <Stat.Root>
                <Stat.Label>{t('admin.main.dashboard.performance.uptime')}</Stat.Label>
                <Stat.ValueText fontSize="lg">{performanceMetrics?.system_uptime || 0}%</Stat.ValueText>
                <Stat.HelpText>
                  {t('admin.main.dashboard.performance.systemAvailability')}
                </Stat.HelpText>
              </Stat.Root>
              <Stat.Root>
                <Stat.Label>{t('admin.main.dashboard.performance.activeSessions')}</Stat.Label>
                <Stat.ValueText fontSize="lg">{performanceMetrics?.active_sessions || 0}</Stat.ValueText>
                <Stat.HelpText>
                  {t('admin.main.dashboard.performance.currentActiveUsers')}
                </Stat.HelpText>
              </Stat.Root>
              <Stat.Root>
                <Stat.Label>{t('admin.main.dashboard.performance.dataProcessed')}</Stat.Label>
                <Stat.ValueText fontSize="lg">{performanceMetrics?.data_processed_gb || 0}GB</Stat.ValueText>
                <Stat.HelpText>
                  {t('admin.main.dashboard.performance.totalDataProcessed')}
                </Stat.HelpText>
              </Stat.Root>
            </SimpleGrid>
          </Card.Body>
        </Card.Root>
      </VStack>
    </Box>
  );
};

// Enhanced System Status component
const SystemStatus = () => {
  const { t } = useTranslation();
  const { data: systemStatus, isLoading, refetch } = useSystemStatus();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const displayStatus = (status?: string) => status ? t(`admin.main.status.${status}`, { defaultValue: status }) : t('admin.main.status.unknown');

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetch();
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const getStatusColor = (service: string, status: string) => {
    const healthyStatuses: Record<string, string[]> = {
      database: ['healthy'],
      ai_model: ['available'],
      embedding: ['operational'],
      redis: ['connected'],
      storage: ['healthy']
    };
    return healthyStatuses[service]?.includes(status) ? 'green' : 'red';
  };

  const getOverallHealthBadge = () => {
    const status = systemStatus?.system_health?.status;
    if (status === 'healthy') return { color: 'green', label: t('admin.main.systemHealth.overall.allOperational') };
    if (status === 'warning') return { color: 'orange', label: t('admin.main.systemHealth.overall.degraded') };
    if (status === 'critical') return { color: 'red', label: t('admin.main.systemHealth.overall.multipleDown') };
    return { color: 'gray', label: t('admin.main.systemHealth.overall.unknown') };
  };

  if (isLoading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="xl" />
        <Text mt={4}>{t('admin.main.systemHealth.loading')}</Text>
      </Box>
    );
  }

  const healthBadge = getOverallHealthBadge();

  return (
    <Box p={6}>
      <VStack gap={6} align="stretch">
        <Flex justify="space-between" align="center">
          <Box>
            <Heading size="lg">{t('admin.main.systemHealth.title')}</Heading>
            <Text color="gray.600" fontSize="sm">{t('admin.main.systemHealth.subtitle')}</Text>
          </Box>
          <HStack gap={3}>
            <Badge 
              colorScheme={healthBadge.color}
              size="lg"
              px={4}
              py={2}
              borderRadius="full"
              fontSize="sm"
            >
              {healthBadge.label}
            </Badge>
            <Button 
              size="sm" 
              variant="outline"
              onClick={handleRefresh}
              loading={isRefreshing}
              loadingText={t('admin.main.common.refreshing')}
            >
              <FiActivity />
              {t('admin.main.common.refresh')}
            </Button>
          </HStack>
        </Flex>

        <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
          {/* Database */}
          <Card.Root bg={cardBg} border="2px" borderColor={getStatusColor('database', systemStatus?.database_status) === 'green' ? 'green.400' : 'red.400'}>
            <Card.Body>
              <HStack justify="space-between">
                <HStack gap={3}>
                  <Icon as={FiDatabase} color="blue.500" boxSize={6} />
                  <VStack align="start" gap={0}>
                    <Text fontWeight="semibold">{t('admin.main.systemHealth.services.database')}</Text>
                    <Text fontSize="xs" color="gray.500">PostgreSQL</Text>
                  </VStack>
                </HStack>
                <Badge 
                  colorScheme={getStatusColor('database', systemStatus?.database_status)}
                  variant="solid"
                  size="lg"
                  px={3}
                  py={1}
                  borderRadius="full"
                >
                  {displayStatus(systemStatus?.database_status)}
                </Badge>
              </HStack>
              {systemStatus?.database && (
                <VStack gap={2} mt={4} align="stretch">
                  <HStack justify="space-between" fontSize="sm">
                    <Text color="gray.600">{t('admin.main.systemHealth.totalUsers')}</Text>
                    <Text fontWeight="medium">{systemStatus.database.total_users}</Text>
                  </HStack>
                  <HStack justify="space-between" fontSize="sm">
                    <Text color="gray.600">{t('admin.main.systemHealth.active24h')}</Text>
                    <Text fontWeight="medium">{systemStatus.database.active_users_24h}</Text>
                  </HStack>
                </VStack>
              )}
            </Card.Body>
          </Card.Root>

          {/* AI Service */}
          <Card.Root bg={cardBg} border="2px" borderColor={getStatusColor('ai_model', systemStatus?.ai_model_status) === 'green' ? 'green.400' : 'red.400'}>
            <Card.Body>
              <HStack justify="space-between">
                <HStack gap={3}>
                  <Icon as={FiCpu} color="purple.500" boxSize={6} />
                  <VStack align="start" gap={0}>
                    <Text fontWeight="semibold">{t('admin.main.systemHealth.services.aiService')}</Text>
                    <Text fontSize="xs" color="gray.500">{t('admin.main.systemHealth.llmModel')}</Text>
                  </VStack>
                </HStack>
                <Badge 
                  colorScheme={getStatusColor('ai_model', systemStatus?.ai_model_status)}
                  variant="solid"
                  size="lg"
                  px={3}
                  py={1}
                  borderRadius="full"
                >
                  {displayStatus(systemStatus?.ai_model_status)}
                </Badge>
              </HStack>
            </Card.Body>
          </Card.Root>

          {/* Vector Database */}
          <Card.Root bg={cardBg} border="2px" borderColor={getStatusColor('embedding', systemStatus?.embedding_service_status) === 'green' ? 'green.400' : 'red.400'}>
            <Card.Body>
              <HStack justify="space-between">
                <HStack gap={3}>
                  <Icon as={FiCloud} color="orange.500" boxSize={6} />
                  <VStack align="start" gap={0}>
                    <Text fontWeight="semibold">{t('admin.main.systemHealth.services.vectorDatabase')}</Text>
                    <Text fontSize="xs" color="gray.500">ChromaDB</Text>
                  </VStack>
                </HStack>
                <Badge 
                  colorScheme={getStatusColor('embedding', systemStatus?.embedding_service_status)}
                  variant="solid"
                  size="lg"
                  px={3}
                  py={1}
                  borderRadius="full"
                >
                  {displayStatus(systemStatus?.embedding_service_status)}
                </Badge>
              </HStack>
            </Card.Body>
          </Card.Root>

          {/* Redis Cache */}
          <Card.Root bg={cardBg} border="2px" borderColor={getStatusColor('redis', systemStatus?.redis_status) === 'green' ? 'green.400' : 'red.400'}>
            <Card.Body>
              <HStack justify="space-between">
                <HStack gap={3}>
                  <Icon as={FiActivity} color="green.500" boxSize={6} />
                  <VStack align="start" gap={0}>
                    <Text fontWeight="semibold">{t('admin.main.systemHealth.services.cacheService')}</Text>
                    <Text fontSize="xs" color="gray.500">Redis</Text>
                  </VStack>
                </HStack>
                <Badge 
                  colorScheme={getStatusColor('redis', systemStatus?.redis_status)}
                  variant="solid"
                  size="lg"
                  px={3}
                  py={1}
                  borderRadius="full"
                >
                  {displayStatus(systemStatus?.redis_status)}
                </Badge>
              </HStack>
            </Card.Body>
          </Card.Root>

          {/* Object Storage */}
          <Card.Root bg={cardBg} border="2px" borderColor={getStatusColor('storage', systemStatus?.storage_status?.status) === 'green' ? 'green.400' : 'red.400'}>
            <Card.Body>
              <HStack justify="space-between">
                <HStack gap={3}>
                  <Icon as={FiCloud} color="cyan.500" boxSize={6} />
                  <VStack align="start" gap={0}>
                    <Text fontWeight="semibold">{t('admin.main.systemHealth.services.objectStorage')}</Text>
                    <Text fontSize="xs" color="gray.500">
                      {systemStatus?.storage_status?.backend === 's3' ? 'S3 / MinIO' : t('admin.main.systemHealth.localFilesystem')}
                    </Text>
                  </VStack>
                </HStack>
                <Badge
                  colorScheme={getStatusColor('storage', systemStatus?.storage_status?.status)}
                  variant="solid"
                  size="lg"
                  px={3}
                  py={1}
                  borderRadius="full"
                >
                  {displayStatus(systemStatus?.storage_status?.status)}
                </Badge>
              </HStack>
              {systemStatus?.storage_status?.backend === 's3' && (
                <VStack gap={2} mt={4} align="stretch">
                  <HStack justify="space-between" fontSize="sm">
                    <Text color="gray.600">{t('admin.main.systemHealth.bucket')}</Text>
                    <Text fontWeight="medium">{systemStatus.storage_status.bucket || t('admin.main.status.unknown')}</Text>
                  </HStack>
                  <HStack justify="space-between" fontSize="sm">
                    <Text color="gray.600">{t('admin.main.systemHealth.endpoint')}</Text>
                    <Text fontWeight="medium" maxW="220px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                      {systemStatus.storage_status.endpoint_url || t('admin.main.systemHealth.defaultAwsEndpoint')}
                    </Text>
                  </HStack>
                </VStack>
              )}
            </Card.Body>
          </Card.Root>
        </SimpleGrid>

        {/* System Resources */}
        {systemStatus?.system && (
          <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
            <Card.Header>
              <Heading size="md">{t('admin.main.systemHealth.systemResources')}</Heading>
            </Card.Header>
            <Card.Body>
              <SimpleGrid columns={{ base: 1, md: 3 }} gap={6}>
                <VStack align="stretch">
                  <Text fontSize="sm" fontWeight="medium" color="gray.600">{t('admin.main.systemHealth.cpuUsage')}</Text>
                  <HStack>
                    <Text fontSize="2xl" fontWeight="bold">{systemStatus.system.cpu_percent}%</Text>
                    <Badge colorScheme={systemStatus.system.cpu_percent > 80 ? 'red' : 'green'}>
                      {systemStatus.system.cpu_percent > 80 ? t('admin.main.status.high') : t('admin.main.status.normal')}
                    </Badge>
                  </HStack>
                </VStack>
                <VStack align="stretch">
                  <Text fontSize="sm" fontWeight="medium" color="gray.600">{t('admin.main.systemHealth.memory')}</Text>
                  <HStack>
                    <Text fontSize="2xl" fontWeight="bold">{systemStatus.system.memory.percent}%</Text>
                    <Text fontSize="sm" color="gray.500">
                      {systemStatus.system.memory.used}GB / {systemStatus.system.memory.total}GB
                    </Text>
                  </HStack>
                </VStack>
                <VStack align="stretch">
                  <Text fontSize="sm" fontWeight="medium" color="gray.600">{t('admin.main.systemHealth.diskSpace')}</Text>
                  <HStack>
                    <Text fontSize="2xl" fontWeight="bold">{systemStatus.system.disk.percent}%</Text>
                    <Text fontSize="sm" color="gray.500">
                      {systemStatus.system.disk.used}GB / {systemStatus.system.disk.total}GB
                    </Text>
                  </HStack>
                </VStack>
              </SimpleGrid>
            </Card.Body>
          </Card.Root>
        )}

        {systemStatus?.system_health && (
          <Card.Root bg="gray.50" border="1px" borderColor="gray.200"size="sm">
            <Card.Body>
              <HStack justify="space-between" fontSize="sm">
                <Text color="gray.600">{t('admin.main.common.lastUpdated')}</Text>
                <Text fontWeight="medium">
                  {new Date(systemStatus.system_health.timestamp).toLocaleString()}
                </Text>
              </HStack>
            </Card.Body>
          </Card.Root>
        )}
      </VStack>
    </Box>
  );
};

// Enhanced Standards Management using MultiStandardUpload
const StandardsManagement = () => {
  const { t } = useTranslation();
  return (
    <Box p={6}>
      <VStack gap={6} align="stretch">
        <Box>
          <Heading size="lg" mb={2}>{t('admin.main.standards.title')}</Heading>
          <Text color="gray.600" fontSize="sm">
            {t('admin.main.standards.subtitle')}
          </Text>
        </Box>
        <MultiStandardUpload />
      </VStack>
    </Box>
  );
};

// Configuration Management Component
const ConfigurationManagement = () => {
  const { t } = useTranslation();
  const { data: appSettings, isLoading: settingsLoading } = useAppSettings();
  const { data: integrations, isLoading: integrationsLoading } = useIntegrations();
  const { data: envInfo, isLoading: envLoading } = useEnvironmentInfo();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const [isSaving, setIsSaving] = useState(false);

  // State for editable configurations
  const [stripeConfig, setStripeConfig] = useState({
    publishableKey: '',
    secretKey: '',
    webhookSecret: ''
  });

  const [emailConfig, setEmailConfig] = useState({
    smtpHost: '',
    smtpPort: '587',
    smtpUser: '',
    smtpPassword: '',
    fromEmail: '',
    fromName: 'Neurev'
  });

  // Real API keys from backend
  const [apiKeys, setApiKeys] = useState<any[]>([]);
  const [isLoadingKeys, setIsLoadingKeys] = useState(true);
  const [showNewKeyDialog, setShowNewKeyDialog] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [generatedKeyData, setGeneratedKeyData] = useState<any>(null);

  // Fetch API keys on mount
  React.useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      setIsLoadingKeys(true);
      const keys = await adminAPI.listApiKeys();
      setApiKeys(keys);
    } catch (error: any) {
      console.error('Failed to fetch API keys:', error);
      const detail = error?.response?.data?.detail;
      const description = typeof detail === 'string' ? detail : detail?.reason || detail?.error || error?.message;
      toaster.error({ title: t('admin.main.config.toasts.failedLoadApiKeys'), description });
    } finally {
      setIsLoadingKeys(false);
    }
  };

  const [ollamaModels, setOllamaModels] = useState<string[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);

  const fetchOllamaModels = async (showToast = false) => {
    setLoadingModels(true);
    try {
      const result = await adminAPI.getOllamaModels();
      setOllamaModels(result.models || []);
      if (showToast) {
        if (result.error) {
          toaster.error({ title: t('admin.main.config.toasts.couldNotReachAiService', { error: result.error }) });
        } else if ((result.models || []).length > 0) {
          toaster.success({ title: t('admin.main.config.toasts.modelsLoaded', { count: result.models.length }) });
        } else {
          toaster.warning({ title: t('admin.main.config.toasts.aiServiceNoModels') });
        }
      }
    } catch (err: any) {
      if (showToast) {
        toaster.error({ title: t('admin.main.config.toasts.couldNotFetchModels'), description: err?.message });
      }
    } finally {
      setLoadingModels(false);
    }
  };

  const [aiRuntimeConfig, setAiRuntimeConfig] = useState({
    ollamaBaseUrl: '',
    ollamaModel: '',
    aiApiKey: '',
    aiApiKeySet: false,
    numCtx: '32768',
    numPredict: '8192',
    temperature: '0.1',
    topP: '0.9',
    topK: '40',
    repeatPenalty: '1.05',
    repeatLastN: '64',
    requestTimeoutSeconds: '600'
  });

  const [embeddingRuntimeConfig, setEmbeddingRuntimeConfig] = useState({
    embeddingModel: 'BAAI/bge-m3',
    semanticCandidatesTopK: '25',
    semanticMaxUsefulChunks: '15',
    semanticMinSimilarity: '0.30',
    semanticMaxContextChars: '40000',
    semanticPrefilterMinSimilarity: '0.20',
    evidenceMaxFilesPerMeasure: '6',
    evidenceMaxCharsPerFile: '2000',
    evidenceMaxFileSizeMb: '15'
  });

  const [embeddingModelsInfo, setEmbeddingModelsInfo] = useState<{
    active_model: string;
    cached_models: string[];
    available_models: string[];
  }>({ active_model: '', cached_models: [], available_models: [] });

  const [aiUsageStats, setAiUsageStats] = useState({
    total_requests: 0,
    cached_hits: 0,
    cache_hit_rate_percent: 0,
    total_prompt_tokens: 0,
    total_completion_tokens: 0,
    total_tokens: 0,
    avg_tokens_per_request: 0,
    last_model: '',
    last_updated_at: ''
  });

  // System settings
  const [systemSettings, setSystemSettings] = useState({
    maintenanceMode: false,
    allowSignups: true,
    requireEmailVerification: true
  });

  // Fetch system settings on mount
  useEffect(() => {
    const fetchSystemSettings = async () => {
      try {
        const settings = await adminAPI.getSystemSettings();
        setSystemSettings({
          maintenanceMode: settings.maintenance_mode,
          allowSignups: settings.allow_signups,
          requireEmailVerification: settings.require_email_verification
        });
      } catch (error) {
        console.error('Failed to fetch system settings:', error);
        toaster.error({ title: t('admin.main.config.toasts.failedLoadSystemSettings') });
      }
    };

    fetchSystemSettings();
  }, []);

  // Fetch AI runtime settings on mount
  useEffect(() => {
    const fetchAISettings = async () => {
      try {
        const settings = await adminAPI.getAISettings();
        setAiRuntimeConfig({
          ollamaBaseUrl: settings.ollama_base_url || '',
          ollamaModel: settings.ollama_model || '',
          aiApiKey: '',
          aiApiKeySet: !!settings.api_key_set,
          numCtx: String(settings.num_ctx ?? '8192'),
          numPredict: String(settings.num_predict ?? '2048'),
          temperature: String(settings.temperature ?? '0.3'),
          topP: String(settings.top_p ?? '0.9'),
          topK: String(settings.top_k ?? '40'),
          repeatPenalty: String(settings.repeat_penalty ?? '1.1'),
          repeatLastN: String(settings.repeat_last_n ?? '64'),
          requestTimeoutSeconds: String(settings.request_timeout_seconds ?? '300')
        });
      } catch (error) {
        console.error('Failed to fetch AI settings:', error);
        toaster.error({ title: t('admin.main.config.toasts.failedLoadAiSettings') });
      }
    };

    fetchAISettings();
    fetchOllamaModels(false); // silent on mount — user can hit Refresh to get toasts
  }, []);

  // Fetch embedding settings and AI usage analytics on mount
  useEffect(() => {
    const fetchEmbeddingAndUsage = async () => {
      try {
        const [embeddingSettings, usageResult, embModels] = await Promise.all([
          adminAPI.getEmbeddingSettings(),
          adminAPI.getAIUsageAnalytics(),
          adminAPI.getEmbeddingModels()
        ]);

        setEmbeddingModelsInfo(embModels);
        setEmbeddingRuntimeConfig({
          embeddingModel: embeddingSettings.embedding_model || embModels.active_model || 'nomic-ai/nomic-embed-text-v1.5',
          semanticCandidatesTopK: String(embeddingSettings.semantic_candidates_top_k ?? '12'),
          semanticMaxUsefulChunks: String(embeddingSettings.semantic_max_useful_chunks ?? '8'),
          semanticMinSimilarity: String(embeddingSettings.semantic_min_similarity ?? '0.35'),
          semanticMaxContextChars: String(embeddingSettings.semantic_max_context_chars ?? '20000'),
          semanticPrefilterMinSimilarity: String(embeddingSettings.semantic_prefilter_min_similarity ?? '0.25'),
          evidenceMaxFilesPerMeasure: String(embeddingSettings.evidence_max_files_per_measure ?? '6'),
          evidenceMaxCharsPerFile: String(embeddingSettings.evidence_max_chars_per_file ?? '2000'),
          evidenceMaxFileSizeMb: String(embeddingSettings.evidence_max_file_size_mb ?? '15')
        });

        if (usageResult?.usage) {
          setAiUsageStats(usageResult.usage);
        }
      } catch (error) {
        console.error('Failed to fetch embedding settings or AI usage analytics:', error);
        toaster.error({ title: t('admin.main.config.toasts.failedLoadEmbeddingUsageSettings') });
      }
    };

    fetchEmbeddingAndUsage();
  }, []);

  // Fetch email settings on mount
  useEffect(() => {
    const fetchEmailSettings = async () => {
      try {
        const settings = await adminAPI.getEmailSettings();
        setEmailConfig({
          smtpHost: settings.smtp_host || '',
          smtpPort: settings.smtp_port || '587',
          smtpUser: settings.smtp_user || '',
          smtpPassword: settings.smtp_password || '',
          fromEmail: settings.mail_from || '',
          fromName: settings.mail_from_name || 'Neurev'
        });
      } catch (error) {
        console.error('Failed to fetch email settings:', error);
      }
    };

    fetchEmailSettings();
  }, []);

  const handleSaveStripe = async () => {
    setIsSaving(true);
    try {
      // TODO: Implement API call to save Stripe configuration
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulated delay
      toaster.success({ title: t('admin.main.config.toasts.stripeSaved') });
    } catch (error) {
      toaster.error({ title: t('admin.main.config.toasts.stripeSaveFailed') });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveEmail = async () => {
    setIsSaving(true);
    try {
      const result = await adminAPI.updateEmailSettings({
        smtp_host: emailConfig.smtpHost,
        smtp_port: emailConfig.smtpPort,
        smtp_user: emailConfig.smtpUser,
        smtp_password: emailConfig.smtpPassword,
        mail_from: emailConfig.fromEmail,
        mail_from_name: emailConfig.fromName
      });
      // Update local state with returned (masked) values
      if (result.settings) {
        setEmailConfig({
          smtpHost: result.settings.smtp_host || '',
          smtpPort: result.settings.smtp_port || '587',
          smtpUser: result.settings.smtp_user || '',
          smtpPassword: result.settings.smtp_password || '',
          fromEmail: result.settings.mail_from || '',
          fromName: result.settings.mail_from_name || 'Neurev'
        });
      }
      toaster.success({ title: t('admin.main.config.toasts.emailSaved') });
    } catch (error) {
      toaster.error({ title: t('admin.main.config.toasts.emailSaveFailed') });
    } finally {
      setIsSaving(false);
    }
  };

  const [isTesting, setIsTesting] = useState(false);

  const handleTestEmail = async () => {
    setIsTesting(true);
    try {
      const result = await adminAPI.testEmailConnection();
      if (result.success) {
        toaster.success({ title: t('admin.main.config.toasts.testEmailSent'), description: result.message });
      } else {
        toaster.error({ title: t('admin.main.config.toasts.testFailed'), description: result.message });
      }
    } catch (error: any) {
      toaster.error({ title: t('admin.main.config.toasts.testFailed'), description: error.response?.data?.detail || t('admin.main.config.toasts.couldNotSendTestEmail') });
    } finally {
      setIsTesting(false);
    }
  };

  const handleGenerateApiKey = async () => {
    if (!newKeyName.trim()) {
      toaster.error({ title: t('admin.main.config.toasts.enterApiKeyName') });
      return;
    }

    setIsSaving(true);
    try {
      const result = await adminAPI.generateApiKey(newKeyName);
      setGeneratedKeyData(result);
      setShowNewKeyDialog(false);
      setNewKeyName('');
      await fetchApiKeys(); // Refresh list
      toaster.success({ 
        title: t('admin.main.config.toasts.apiKeyGenerated'), 
        description: 'Make sure to copy it now - you won\'t be able to see it again!' 
      });
    } catch (error: any) {
      console.error('Failed to generate API key:', error);
      const detail = error?.response?.data?.detail;
      const description = typeof detail === 'string'
        ? detail
        : detail?.reason || detail?.error || error?.message;
      toaster.error({ title: t('admin.main.config.toasts.apiKeyGenerateFailed'), description });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRevokeApiKey = async (keyId: number) => {
    if (!confirm(t('admin.main.config.confirmRevokeApiKey'))) {
      return;
    }

    try {
      await adminAPI.revokeApiKey(keyId);
      await fetchApiKeys(); // Refresh list
      toaster.success({ title: t('admin.main.config.toasts.apiKeyRevoked') });
    } catch (error: any) {
      console.error('Failed to revoke API key:', error);
      const detail = error?.response?.data?.detail;
      const description = typeof detail === 'string' ? detail : detail?.reason || detail?.error || error?.message;
      toaster.error({ title: t('admin.main.config.toasts.apiKeyRevokeFailed'), description });
    }
  };

  const handleSaveAiProviders = async () => {
    setIsSaving(true);
    try {
      const result = await adminAPI.updateAISettings({
        ollama_base_url: aiRuntimeConfig.ollamaBaseUrl.trim(),
        ollama_model: aiRuntimeConfig.ollamaModel.trim(),
        api_key: aiRuntimeConfig.aiApiKey.trim() || undefined,
        num_ctx: Number(aiRuntimeConfig.numCtx),
        num_predict: Number(aiRuntimeConfig.numPredict),
        temperature: Number(aiRuntimeConfig.temperature),
        top_p: Number(aiRuntimeConfig.topP),
        top_k: Number(aiRuntimeConfig.topK),
        repeat_penalty: Number(aiRuntimeConfig.repeatPenalty),
        repeat_last_n: Number(aiRuntimeConfig.repeatLastN),
        request_timeout_seconds: Number(aiRuntimeConfig.requestTimeoutSeconds)
      });

      if (result?.settings) {
        setAiRuntimeConfig({
          ollamaBaseUrl: result.settings.ollama_base_url || '',
          ollamaModel: result.settings.ollama_model || '',
          aiApiKey: '',
          aiApiKeySet: !!result.settings.api_key_set,
          numCtx: String(result.settings.num_ctx ?? aiRuntimeConfig.numCtx),
          numPredict: String(result.settings.num_predict ?? aiRuntimeConfig.numPredict),
          temperature: String(result.settings.temperature ?? aiRuntimeConfig.temperature),
          topP: String(result.settings.top_p ?? aiRuntimeConfig.topP),
          topK: String(result.settings.top_k ?? aiRuntimeConfig.topK),
          repeatPenalty: String(result.settings.repeat_penalty ?? aiRuntimeConfig.repeatPenalty),
          repeatLastN: String(result.settings.repeat_last_n ?? aiRuntimeConfig.repeatLastN),
          requestTimeoutSeconds: String(result.settings.request_timeout_seconds ?? aiRuntimeConfig.requestTimeoutSeconds)
        });
      }

      toaster.success({ title: t('admin.main.config.toasts.aiRuntimeSaved') });
    } catch (error) {
      toaster.error({ title: t('admin.main.config.toasts.aiRuntimeSaveFailed') });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveSystemSettings = async () => {
    setIsSaving(true);
    try {
      // Convert camelCase to snake_case for API
      const apiSettings = {
        maintenance_mode: systemSettings.maintenanceMode,
        allow_signups: systemSettings.allowSignups,
        require_email_verification: systemSettings.requireEmailVerification
      };
      
      await adminAPI.updateSystemSettings(apiSettings);
      toaster.success({ title: t('admin.main.config.toasts.systemSettingsSaved') });
    } catch (error) {
      console.error('Failed to save system settings:', error);
      toaster.error({ title: t('admin.main.config.toasts.systemSettingsSaveFailed') });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveEmbeddingSettings = async () => {
    setIsSaving(true);
    try {
      const result = await adminAPI.updateEmbeddingSettings({
        embedding_model: embeddingRuntimeConfig.embeddingModel.trim(),
        semantic_candidates_top_k: Number(embeddingRuntimeConfig.semanticCandidatesTopK),
        semantic_max_useful_chunks: Number(embeddingRuntimeConfig.semanticMaxUsefulChunks),
        semantic_min_similarity: Number(embeddingRuntimeConfig.semanticMinSimilarity),
        semantic_max_context_chars: Number(embeddingRuntimeConfig.semanticMaxContextChars),
        semantic_prefilter_min_similarity: Number(embeddingRuntimeConfig.semanticPrefilterMinSimilarity),
        evidence_max_files_per_measure: Number(embeddingRuntimeConfig.evidenceMaxFilesPerMeasure),
        evidence_max_chars_per_file: Number(embeddingRuntimeConfig.evidenceMaxCharsPerFile),
        evidence_max_file_size_mb: Number(embeddingRuntimeConfig.evidenceMaxFileSizeMb)
      });

      if (result?.settings) {
        setEmbeddingRuntimeConfig({
          embeddingModel: result.settings.embedding_model || embeddingRuntimeConfig.embeddingModel,
          semanticCandidatesTopK: String(result.settings.semantic_candidates_top_k ?? embeddingRuntimeConfig.semanticCandidatesTopK),
          semanticMaxUsefulChunks: String(result.settings.semantic_max_useful_chunks ?? embeddingRuntimeConfig.semanticMaxUsefulChunks),
          semanticMinSimilarity: String(result.settings.semantic_min_similarity ?? embeddingRuntimeConfig.semanticMinSimilarity),
          semanticMaxContextChars: String(result.settings.semantic_max_context_chars ?? embeddingRuntimeConfig.semanticMaxContextChars),
          semanticPrefilterMinSimilarity: String(result.settings.semantic_prefilter_min_similarity ?? embeddingRuntimeConfig.semanticPrefilterMinSimilarity),
          evidenceMaxFilesPerMeasure: String(result.settings.evidence_max_files_per_measure ?? embeddingRuntimeConfig.evidenceMaxFilesPerMeasure),
          evidenceMaxCharsPerFile: String(result.settings.evidence_max_chars_per_file ?? embeddingRuntimeConfig.evidenceMaxCharsPerFile),
          evidenceMaxFileSizeMb: String(result.settings.evidence_max_file_size_mb ?? embeddingRuntimeConfig.evidenceMaxFileSizeMb)
        });
      }

      toaster.success({ title: t('admin.main.config.toasts.embeddingSaved') });
    } catch (error) {
      console.error('Failed to save embedding settings:', error);
      toaster.error({ title: t('admin.main.config.toasts.embeddingSaveFailed') });
    } finally {
      setIsSaving(false);
    }
  };

  const [reindexStatus, setReindexStatus] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [reindexResult, setReindexResult] = useState<string>('');

  const handleReindexChromaDB = async () => {
    setReindexStatus('running');
    setReindexResult('');
    try {
      const res = await adminAPI.triggerReindex();
      const { system_standards, policies } = res.results;
      const stdOk = (system_standards || []).filter((s: any) => s.status === 'ok').length;
      const stdTotal = (system_standards || []).length;
      setReindexResult(
        t('admin.main.config.reindexResult', { stdOk, stdTotal, indexed: policies.indexed, failed: policies.failed })
      );
      setReindexStatus('done');
      toaster.success({ title: t('admin.main.config.toasts.chromadbReindexed') });
    } catch (err: any) {
      setReindexStatus('error');
      setReindexResult(err?.response?.data?.detail || err?.message || t('admin.main.status.unknownError'));
      toaster.error({ title: t('admin.main.config.toasts.reindexingFailed'), description: reindexResult });
    }
  };

  const handleRefreshAIUsageStats = async () => {
    try {
      const usageResult = await adminAPI.getAIUsageAnalytics();
      if (usageResult?.usage) {
        setAiUsageStats(usageResult.usage);
      }
      toaster.success({ title: t('admin.main.config.toasts.aiUsageStatsRefreshed') });
    } catch (error) {
      console.error('Failed to refresh AI usage stats:', error);
      toaster.error({ title: t('admin.main.config.toasts.aiUsageStatsRefreshFailed') });
    }
  };

  if (settingsLoading || integrationsLoading || envLoading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="xl" />
        <Text mt={4}>{t('admin.main.config.loading')}</Text>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <VStack gap={6} align="stretch">
        <Box>
          <Heading size="lg">{t('admin.main.config.title')}</Heading>
          <Text color="gray.600">{t('admin.main.config.subtitle')}</Text>
        </Box>

        {/* Payment Configuration - Stripe */}
        <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
          <Card.Header bg="purple.50" borderBottom="1px" borderColor={borderColor}>
            <HStack justify="space-between">
              <HStack>
                <Icon color="purple.600">
                  <FiCreditCard />
                </Icon>
                <Heading size="md">{t('admin.main.config.paymentSettings')}</Heading>
              </HStack>
              <Badge colorScheme={integrations?.stripe?.status === 'connected' ? 'green' : 'red'}>
                {integrations?.stripe?.status === 'connected' ? t('admin.main.status.connected') : t('admin.main.status.notConfigured')}
              </Badge>
            </HStack>
          </Card.Header>
          <Card.Body>
            <form onSubmit={(e) => e.preventDefault()}>
            <VStack gap={4} align="stretch">
              <Field.Root>
                <Field.Label>{t('admin.main.config.publishableKey')}</Field.Label>
                <Input 
                  placeholder="pk_live_..." 
                  value={stripeConfig.publishableKey}
                  onChange={(e) => setStripeConfig({...stripeConfig, publishableKey: e.target.value})}
                  fontFamily="mono"
                  fontSize="sm"
                />
                <Field.HelperText>{t('admin.main.config.stripePublishableKeyHelp')}</Field.HelperText>
              </Field.Root>

              <Field.Root>
                <Field.Label>{t('admin.main.config.secretKey')}</Field.Label>
                <Input 
                  type="password"
                  placeholder="sk_live_..." 
                  value={stripeConfig.secretKey}
                  onChange={(e) => setStripeConfig({...stripeConfig, secretKey: e.target.value})}
                  fontFamily="mono"
                  fontSize="sm"
                />
                <Field.HelperText>{t('admin.main.config.stripeSecretKeyHelp')}</Field.HelperText>
              </Field.Root>

              <Field.Root>
                <Field.Label>{t('admin.main.config.webhookSecret')}</Field.Label>
                <Input 
                  type="password"
                  placeholder="whsec_..." 
                  value={stripeConfig.webhookSecret}
                  onChange={(e) => setStripeConfig({...stripeConfig, webhookSecret: e.target.value})}
                  fontFamily="mono"
                  fontSize="sm"
                />
                <Field.HelperText>{t('admin.main.config.webhookSecretHelp')}</Field.HelperText>
              </Field.Root>

              <HStack justify="flex-end">
                <Button 
                  colorScheme="purple" 
                  onClick={handleSaveStripe}
                  loading={isSaving}
                >
                  <FiKey />
                  {t('admin.main.config.saveStripeConfiguration')}
                </Button>
              </HStack>
            </VStack>
            </form>
          </Card.Body>
        </Card.Root>

        {/* Email Server Configuration */}
        <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
          <Card.Header bg="blue.50" borderBottom="1px" borderColor={borderColor}>
            <HStack justify="space-between">
              <HStack>
                <Icon color="blue.600">
                  <FiLifeBuoy />
                </Icon>
                <Heading size="md">{t('admin.main.config.emailServerSettings')}</Heading>
              </HStack>
              <Badge colorScheme={integrations?.email?.status === 'configured' ? 'green' : 'orange'}>
                {integrations?.email?.status === 'configured' ? t('admin.main.status.configured') : t('admin.main.status.notConfigured')}
              </Badge>
            </HStack>
          </Card.Header>
          <Card.Body>
            <form onSubmit={(e) => e.preventDefault()}>
            <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
              <Field.Root>
                <Field.Label>{t('admin.main.config.smtpHost')}</Field.Label>
                <Input 
                  placeholder="smtp.gmail.com" 
                  value={emailConfig.smtpHost}
                  onChange={(e) => setEmailConfig({...emailConfig, smtpHost: e.target.value})}
                />
              </Field.Root>

              <Field.Root>
                <Field.Label>{t('admin.main.config.smtpPort')}</Field.Label>
                <Input 
                  type="number"
                  placeholder="587" 
                  value={emailConfig.smtpPort}
                  onChange={(e) => setEmailConfig({...emailConfig, smtpPort: e.target.value})}
                />
              </Field.Root>

              <Field.Root>
                <Field.Label>{t('admin.main.config.smtpUsername')}</Field.Label>
                <Input 
                  placeholder="your-email@domain.com" 
                  value={emailConfig.smtpUser}
                  onChange={(e) => setEmailConfig({...emailConfig, smtpUser: e.target.value})}
                />
              </Field.Root>

              <Field.Root>
                <Field.Label>{t('admin.main.config.smtpPassword')}</Field.Label>
                <Input 
                  type="password"
                  placeholder="••••••••" 
                  value={emailConfig.smtpPassword}
                  onChange={(e) => setEmailConfig({...emailConfig, smtpPassword: e.target.value})}
                />
              </Field.Root>

              <Field.Root>
                <Field.Label>{t('admin.main.config.fromEmail')}</Field.Label>
                <Input 
                  type="email"
                  placeholder="noreply@yourdomain.com" 
                  value={emailConfig.fromEmail}
                  onChange={(e) => setEmailConfig({...emailConfig, fromEmail: e.target.value})}
                />
              </Field.Root>

              <Field.Root>
                <Field.Label>{t('admin.main.config.fromName')}</Field.Label>
                <Input 
                  placeholder="Neurev" 
                  value={emailConfig.fromName}
                  onChange={(e) => setEmailConfig({...emailConfig, fromName: e.target.value})}
                />
              </Field.Root>
            </SimpleGrid>

            <HStack justify="flex-end" mt={4}>
              <Button 
                variant="outline" 
                onClick={handleTestEmail}
                loading={isTesting}
                disabled={!emailConfig.smtpHost}
              >
                {t('admin.main.config.testConnection')}
              </Button>
              <Button 
                colorScheme="blue" 
                onClick={handleSaveEmail}
                loading={isSaving}
              >
                {t('admin.main.config.saveEmailConfiguration')}
              </Button>
            </HStack>
            </form>
          </Card.Body>
        </Card.Root>

        {/* Neurev API Keys - For external integrations */}
        <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
          <Card.Header bg="green.50" borderBottom="1px" borderColor={borderColor}>
            <HStack justify="space-between">
              <HStack>
                <Icon color="green.600">
                  <FiKey />
                </Icon>
                <Heading size="md">{t('admin.main.config.neurevApiKeys')}</Heading>
              </HStack>
              <Button 
                size="sm"
                colorScheme="green" 
                onClick={() => setShowNewKeyDialog(true)}
                loading={isSaving}
              >
                <FiPlus />
                {t('admin.main.config.generateNewKey')}
              </Button>
            </HStack>
          </Card.Header>
          <Card.Body>
            <VStack gap={4} align="stretch">
              <Text fontSize="sm" color="gray.600">
                {t('admin.main.config.apiKeysDescription')}
              </Text>

              {isLoadingKeys ? (
                <Box p={8} textAlign="center">
                  <Spinner size="lg" />
                  <Text mt={2} color="gray.500">{t('admin.main.config.loadingApiKeys')}</Text>
                </Box>
              ) : apiKeys.length === 0 ? (
                <Box p={8} textAlign="center" bg="gray.50" borderRadius="md">
                  <Text color="gray.500">{t('admin.main.config.noApiKeys')}</Text>
                  <Text fontSize="sm" color="gray.400" mt={1}>{t('admin.main.config.createFirstApiKeyHint')}</Text>
                </Box>
              ) : (
                <VStack gap={3} align="stretch">
                  {apiKeys.map(apiKey => {
                    const createdDate = new Date(apiKey.created_at).toLocaleDateString();
                    const lastUsed = apiKey.last_used_at 
                      ? new Date(apiKey.last_used_at).toLocaleString()
                      : t('admin.main.common.never');
                    
                    return (
                    <HStack 
                      key={apiKey.id} 
                      justify="space-between" 
                      p={4} 
                      bg={apiKey.is_active ? 'gray.50' : 'red.50'} 
                      borderRadius="md"
                      border="1px"
                      borderColor={apiKey.is_active ? 'gray.200' : 'red.200'}
                    >
                      <VStack align="start" gap={1} flex={1}>
                        <HStack>
                          <Text fontWeight="medium">{apiKey.name}</Text>
                          {!apiKey.is_active && (
                            <Badge colorScheme="red" size="sm">{t('admin.main.status.revoked')}</Badge>
                          )}
                        </HStack>
                        <Text fontSize="xs" fontFamily="mono" color="gray.600">
                          {apiKey.key_prefix}{'*'.repeat(40)}
                        </Text>
                        <HStack fontSize="xs" color="gray.500">
                          <Text>{t('admin.main.config.createdDate', { date: createdDate })}</Text>
                          <Text>•</Text>
                          <Text>{t('admin.main.config.lastUsedDate', { date: lastUsed })}</Text>
                        </HStack>
                      </VStack>
                      {apiKey.is_active && (
                        <HStack>
                          <IconButton 
                            size="sm" 
                            variant="ghost" 
                            colorScheme="red"
                            onClick={() => handleRevokeApiKey(apiKey.id)}
                          >
                            <FiTrash2 />
                          </IconButton>
                        </HStack>
                      )}
                    </HStack>
                  )})}
                </VStack>
              )}

              <Box p={4} bg="blue.50" borderRadius="md" mt={2}>
                <HStack gap={2}>
                  <Icon color="blue.600">
                    <FiBookOpen />
                  </Icon>
                  <VStack align="start" gap={1}>
                    <Text fontSize="sm" fontWeight="medium" color="blue.900">{t('admin.main.config.apiDocumentation')}</Text>
                    <Text fontSize="xs" color="blue.700">
                      {t('admin.main.config.apiDocsHelp')} <Text as="span" fontFamily="mono" fontWeight="bold">https://docs.neurev.com/api</Text>
                    </Text>
                  </VStack>
                </HStack>
              </Box>
            </VStack>
          </Card.Body>
        </Card.Root>

        {/* AI Provider Settings - Backend configuration */}
        <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
          <Card.Header bg="cyan.50" borderBottom="1px" borderColor={borderColor}>
            <HStack>
              <Icon color="cyan.600">
                <FiCpu />
              </Icon>
              <Heading size="md">{t('admin.main.config.aiProviderSettings')}</Heading>
            </HStack>
          </Card.Header>
          <Card.Body>
            <form onSubmit={(e) => e.preventDefault()}>
            <VStack gap={4} align="stretch">
              <Text fontSize="sm" color="gray.600" mb={2}>
                {t('admin.main.config.aiProviderDescription')}
              </Text>

              <Field.Root>
                <Field.Label>{t('admin.main.config.aiServiceUrl')}</Field.Label>
                <Input 
                  placeholder="https://api.openai.com/v1" 
                  value={aiRuntimeConfig.ollamaBaseUrl}
                  onChange={(e) => setAiRuntimeConfig({...aiRuntimeConfig, ollamaBaseUrl: e.target.value})}
                  fontFamily="mono"
                  fontSize="sm"
                />
                <Field.HelperText>{t('admin.main.config.aiServiceUrlHelp')}</Field.HelperText>
              </Field.Root>

              <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                <Field.Root>
                  <Field.Label>{t('admin.main.config.apiKey')}</Field.Label>
                  <Input
                    type="password"
                    placeholder={aiRuntimeConfig.aiApiKeySet ? t('admin.main.config.storedApiKeyPlaceholder') : t('admin.main.config.enterProviderApiKeyPlaceholder')}
                    value={aiRuntimeConfig.aiApiKey}
                    onChange={(e) => setAiRuntimeConfig({...aiRuntimeConfig, aiApiKey: e.target.value})}
                    fontFamily="mono"
                    fontSize="sm"
                  />
                  <Field.HelperText>
                    {aiRuntimeConfig.aiApiKeySet
                      ? t('admin.main.config.keyAlreadyStoredHelp')
                      : t('admin.main.config.cloudProviderAuthHelp')}
                  </Field.HelperText>
                </Field.Root>

                <Field.Root>
                  <Field.Label>
                    <HStack justify="space-between" w="full">
                      <Text>{t('admin.main.config.model')}</Text>
                      <Button
                        size="xs"
                        variant="ghost"
                        onClick={() => fetchOllamaModels(true)}
                        loading={loadingModels}
                        loadingText={t('admin.main.common.loading')}
                      >
                        <FiRefreshCw />
                        {t('admin.main.common.refresh')}
                      </Button>
                    </HStack>
                  </Field.Label>
                  {ollamaModels.length > 0 ? (
                    <NativeSelect.Root>
                      <NativeSelect.Field
                        value={aiRuntimeConfig.ollamaModel}
                        onChange={(e) => setAiRuntimeConfig({...aiRuntimeConfig, ollamaModel: e.target.value})}
                        fontFamily="mono"
                        fontSize="sm"
                      >
                        <option value="">{t('admin.main.config.selectModel')}</option>
                        {ollamaModels.map((m) => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </NativeSelect.Field>
                    </NativeSelect.Root>
                  ) : (
                    <Input
                      placeholder="gpt-4o"
                      value={aiRuntimeConfig.ollamaModel}
                      onChange={(e) => setAiRuntimeConfig({...aiRuntimeConfig, ollamaModel: e.target.value})}
                      fontFamily="mono"
                      fontSize="sm"
                    />
                  )}
                  <Field.HelperText>
                    {ollamaModels.length > 0
                      ? t('admin.main.config.modelsFound', { count: ollamaModels.length })
                      : t('admin.main.config.clickRefreshLoadModels')}
                  </Field.HelperText>
                </Field.Root>

                <Field.Root>
                  <Field.Label>{t('admin.main.config.contextSize')}</Field.Label>
                  <Input
                    type="number"
                    min={256}
                    max={131072}
                    value={aiRuntimeConfig.numCtx}
                    onChange={(e) => setAiRuntimeConfig({...aiRuntimeConfig, numCtx: e.target.value})}
                  />
                  <Field.HelperText>{t('admin.main.config.contextSizeHelp')}</Field.HelperText>
                </Field.Root>

                <Field.Root>
                  <Field.Label>{t('admin.main.config.maxOutputTokens')}</Field.Label>
                  <Input
                    type="number"
                    min={1}
                    value={aiRuntimeConfig.numPredict}
                    onChange={(e) => setAiRuntimeConfig({...aiRuntimeConfig, numPredict: e.target.value})}
                  />
                </Field.Root>

                <Field.Root>
                  <Field.Label>{t('admin.main.config.temperature')}</Field.Label>
                  <Input
                    type="number"
                    step="0.01"
                    min={0}
                    max={2}
                    value={aiRuntimeConfig.temperature}
                    onChange={(e) => setAiRuntimeConfig({...aiRuntimeConfig, temperature: e.target.value})}
                  />
                </Field.Root>

                <Field.Root>
                  <Field.Label>{t('admin.main.config.topP')}</Field.Label>
                  <Input
                    type="number"
                    step="0.01"
                    min={0}
                    max={1}
                    value={aiRuntimeConfig.topP}
                    onChange={(e) => setAiRuntimeConfig({...aiRuntimeConfig, topP: e.target.value})}
                  />
                </Field.Root>

                <Field.Root>
                  <Field.Label>{t('admin.main.config.topK')}</Field.Label>
                  <Input
                    type="number"
                    min={1}
                    value={aiRuntimeConfig.topK}
                    onChange={(e) => setAiRuntimeConfig({...aiRuntimeConfig, topK: e.target.value})}
                  />
                </Field.Root>

                <Field.Root>
                  <Field.Label>{t('admin.main.config.repeatPenalty')}</Field.Label>
                  <Input
                    type="number"
                    step="0.01"
                    min={1}
                    max={2}
                    value={aiRuntimeConfig.repeatPenalty}
                    onChange={(e) => setAiRuntimeConfig({...aiRuntimeConfig, repeatPenalty: e.target.value})}
                  />
                </Field.Root>

                <Field.Root>
                  <Field.Label>{t('admin.main.config.repeatLastN')}</Field.Label>
                  <Input
                    type="number"
                    min={0}
                    value={aiRuntimeConfig.repeatLastN}
                    onChange={(e) => setAiRuntimeConfig({...aiRuntimeConfig, repeatLastN: e.target.value})}
                  />
                </Field.Root>

                <Field.Root>
                  <Field.Label>{t('admin.main.config.requestTimeoutSeconds')}</Field.Label>
                  <Input
                    type="number"
                    min={10}
                    max={3600}
                    value={aiRuntimeConfig.requestTimeoutSeconds}
                    onChange={(e) => setAiRuntimeConfig({...aiRuntimeConfig, requestTimeoutSeconds: e.target.value})}
                  />
                  <Field.HelperText>{t('admin.main.config.requestTimeoutSecondsHelp')}</Field.HelperText>
                </Field.Root>
              </SimpleGrid>

              <Text fontSize="sm" color="gray.600">
                {t('admin.main.config.aiDefaultsHelp')}
              </Text>

              <Field.Root>
                <Field.Label>{t('admin.main.config.quickPreset')}</Field.Label>
                <NativeSelect.Root>
                  <NativeSelect.Field
                    defaultValue=""
                    onChange={(e) => {
                      const preset = e.target.value;
                      if (preset === 'balanced') {
                        setAiRuntimeConfig({
                          ...aiRuntimeConfig,
                          numCtx: '32768',
                          numPredict: '8192',
                          temperature: '0.1',
                          topP: '0.9',
                          topK: '40',
                          repeatPenalty: '1.05',
                          repeatLastN: '64',
                          requestTimeoutSeconds: '600'
                        });
                      }
                      if (preset === 'deterministic') {
                        setAiRuntimeConfig({
                          ...aiRuntimeConfig,
                          temperature: '0.0',
                          topP: '0.8',
                          topK: '20',
                          repeatPenalty: '1.05'
                        });
                      }
                      if (preset === 'creative') {
                        setAiRuntimeConfig({
                          ...aiRuntimeConfig,
                          temperature: '0.6',
                          topP: '0.95',
                          topK: '60',
                          repeatPenalty: '1.1'
                        });
                      }
                    }}
                  >
                    <option value="" disabled>{t('admin.main.config.selectPreset')}</option>
                    <option value="balanced">{t('admin.main.config.presetBalanced')}</option>
                    <option value="deterministic">{t('admin.main.config.presetDeterministic')}</option>
                    <option value="creative">{t('admin.main.config.presetCreative')}</option>
                  </NativeSelect.Field>
                </NativeSelect.Root>
              </Field.Root>

              <HStack justify="flex-end">
                <Button 
                  colorScheme="cyan" 
                  onClick={handleSaveAiProviders}
                  loading={isSaving}
                >
                  {t('admin.main.config.saveAiProviderSettings')}
                </Button>
              </HStack>
            </VStack>
            </form>
          </Card.Body>
        </Card.Root>

        {/* Embedding / Semantic Retrieval Settings */}
        <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
          <Card.Header bg="teal.50" borderBottom="1px" borderColor={borderColor}>
            <HStack justify="space-between">
              <HStack>
                <Icon color="teal.600">
                  <FiDatabase />
                </Icon>
                <Heading size="md">{t('admin.main.config.embeddingRetrievalSettings')}</Heading>
              </HStack>
              {embeddingModelsInfo.active_model && (
                <Badge colorScheme="teal" variant="subtle" fontSize="xs" px={2} py={1}>
                  {t('admin.main.config.activeModel', { model: embeddingModelsInfo.active_model.split('/').pop() })}
                </Badge>
              )}
            </HStack>
          </Card.Header>
          <Card.Body>
            <VStack gap={4} align="stretch">
              <Text fontSize="sm" color="gray.600">
                {t('admin.main.config.embeddingDescription')}
              </Text>

              <Field.Root>
                <Field.Label>{t('admin.main.config.embeddingModel')}</Field.Label>
                <NativeSelect.Root>
                  <NativeSelect.Field
                    value={embeddingRuntimeConfig.embeddingModel}
                    onChange={(e) => setEmbeddingRuntimeConfig({...embeddingRuntimeConfig, embeddingModel: e.target.value})}
                    fontFamily="mono"
                    fontSize="sm"
                  >
                    {embeddingModelsInfo.cached_models.length > 0 && (
                      <optgroup label={t('admin.main.config.downloadedInstalled')}>
                        {embeddingModelsInfo.cached_models.map((m) => (
                          <option key={m} value={m}>
                            {m === embeddingModelsInfo.active_model ? `✓ ${m}` : m}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    <optgroup label={t('admin.main.config.availableRequiresDownload')}>
                      {embeddingModelsInfo.available_models
                        .filter((m) => !embeddingModelsInfo.cached_models.includes(m))
                        .map((m) => (
                          <option key={m} value={m}>
                            {m === embeddingModelsInfo.active_model ? `✓ ${m}` : m}
                          </option>
                        ))}
                    </optgroup>
                  </NativeSelect.Field>
                </NativeSelect.Root>
                <Field.HelperText>
                  {t('admin.main.config.modelsCachedHelp')}
                </Field.HelperText>
                <HStack mt={2} gap={3} align="center" flexWrap="wrap">
                  <Button
                    size="sm"
                    colorScheme="orange"
                    variant="outline"
                    onClick={handleReindexChromaDB}
                    loading={reindexStatus === 'running'}
                    loadingText={t('admin.main.config.reindexing')}
                  >
                    <FiRefreshCw />
                    {t('admin.main.config.reindexChromadb')}
                  </Button>
                  {reindexStatus === 'done' && (
                    <Text fontSize="xs" color="green.600">{reindexResult}</Text>
                  )}
                  {reindexStatus === 'error' && (
                    <Text fontSize="xs" color="red.600">{reindexResult}</Text>
                  )}
                  {reindexStatus === 'idle' && (
                    <Text fontSize="xs" color="gray.500">{t('admin.main.config.reindexHelp')}</Text>
                  )}
                </HStack>
              </Field.Root>

              <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                <Field.Root>
                  <Field.Label>{t('admin.main.config.semanticCandidatesTopK')}</Field.Label>
                  <Input
                    type="number"
                    min={1}
                    max={100}
                    value={embeddingRuntimeConfig.semanticCandidatesTopK}
                    onChange={(e) => setEmbeddingRuntimeConfig({...embeddingRuntimeConfig, semanticCandidatesTopK: e.target.value})}
                  />
                  <Field.HelperText>{t('admin.main.config.semanticCandidatesTopKHelp')}</Field.HelperText>
                </Field.Root>

                <Field.Root>
                  <Field.Label>{t('admin.main.config.maxUsefulChunks')}</Field.Label>
                  <Input
                    type="number"
                    min={1}
                    max={50}
                    value={embeddingRuntimeConfig.semanticMaxUsefulChunks}
                    onChange={(e) => setEmbeddingRuntimeConfig({...embeddingRuntimeConfig, semanticMaxUsefulChunks: e.target.value})}
                  />
                  <Field.HelperText>{t('admin.main.config.maxUsefulChunksHelp')}</Field.HelperText>
                </Field.Root>

                <Field.Root>
                  <Field.Label>{t('admin.main.config.minimumSimilarity')}</Field.Label>
                  <Input
                    type="number"
                    step="0.01"
                    min={0}
                    max={1}
                    value={embeddingRuntimeConfig.semanticMinSimilarity}
                    onChange={(e) => setEmbeddingRuntimeConfig({...embeddingRuntimeConfig, semanticMinSimilarity: e.target.value})}
                  />
                  <Field.HelperText>{t('admin.main.config.minimumSimilarityHelp')}</Field.HelperText>
                </Field.Root>

                <Field.Root>
                  <Field.Label>{t('admin.main.config.prefilterMinimumSimilarity')}</Field.Label>
                  <Input
                    type="number"
                    step="0.01"
                    min={0}
                    max={1}
                    value={embeddingRuntimeConfig.semanticPrefilterMinSimilarity}
                    onChange={(e) => setEmbeddingRuntimeConfig({...embeddingRuntimeConfig, semanticPrefilterMinSimilarity: e.target.value})}
                  />
                  <Field.HelperText>{t('admin.main.config.prefilterMinimumSimilarityHelp')}</Field.HelperText>
                </Field.Root>

                <Field.Root>
                  <Field.Label>{t('admin.main.config.maxContextCharacters')}</Field.Label>
                  <Input
                    type="number"
                    min={500}
                    max={200000}
                    value={embeddingRuntimeConfig.semanticMaxContextChars}
                    onChange={(e) => setEmbeddingRuntimeConfig({...embeddingRuntimeConfig, semanticMaxContextChars: e.target.value})}
                  />
                  <Field.HelperText>{t('admin.main.config.maxContextCharactersHelp')}</Field.HelperText>
                </Field.Root>

                <Field.Root>
                  <Field.Label>{t('admin.main.config.evidenceMaxFilesPerMeasure')}</Field.Label>
                  <Input
                    type="number"
                    min={1}
                    max={20}
                    value={embeddingRuntimeConfig.evidenceMaxFilesPerMeasure}
                    onChange={(e) => setEmbeddingRuntimeConfig({...embeddingRuntimeConfig, evidenceMaxFilesPerMeasure: e.target.value})}
                  />
                  <Field.HelperText>{t('admin.main.config.evidenceMaxFilesPerMeasureHelp')}</Field.HelperText>
                </Field.Root>

                <Field.Root>
                  <Field.Label>{t('admin.main.config.evidenceMaxCharsPerFile')}</Field.Label>
                  <Input
                    type="number"
                    min={500}
                    max={10000}
                    value={embeddingRuntimeConfig.evidenceMaxCharsPerFile}
                    onChange={(e) => setEmbeddingRuntimeConfig({...embeddingRuntimeConfig, evidenceMaxCharsPerFile: e.target.value})}
                  />
                  <Field.HelperText>{t('admin.main.config.evidenceMaxCharsPerFileHelp')}</Field.HelperText>
                </Field.Root>

                <Field.Root>
                  <Field.Label>{t('admin.main.config.evidenceMaxFileSizeMb')}</Field.Label>
                  <Input
                    type="number"
                    min={1}
                    max={25}
                    value={embeddingRuntimeConfig.evidenceMaxFileSizeMb}
                    onChange={(e) => setEmbeddingRuntimeConfig({...embeddingRuntimeConfig, evidenceMaxFileSizeMb: e.target.value})}
                  />
                  <Field.HelperText>{t('admin.main.config.evidenceMaxFileSizeMbHelp')}</Field.HelperText>
                </Field.Root>
              </SimpleGrid>

              <HStack justify="flex-end">
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="gray"
                  onClick={() => setEmbeddingRuntimeConfig({
                    ...embeddingRuntimeConfig,
                    semanticCandidatesTopK: '25',
                    semanticMaxUsefulChunks: '15',
                    semanticMinSimilarity: '0.30',
                    semanticMaxContextChars: '40000',
                    semanticPrefilterMinSimilarity: '0.20',
                  })}
                >
                  {t('admin.main.config.resetToDefaults')}
                </Button>
              </HStack>

              <Text fontSize="sm" color="gray.600">
                {t('admin.main.config.embeddingTradeoffHelp')}
              </Text>

              <HStack justify="flex-end">
                <Button
                  colorScheme="teal"
                  onClick={handleSaveEmbeddingSettings}
                  loading={isSaving}
                >
                  {t('admin.main.config.saveEmbeddingSettings')}
                </Button>
              </HStack>
            </VStack>
          </Card.Body>
        </Card.Root>

        {/* LLM Token Usage */}
        <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
          <Card.Header bg="purple.50" borderBottom="1px" borderColor={borderColor}>
            <HStack justify="space-between">
              <HStack>
                <Icon color="purple.600">
                  <FiActivity />
                </Icon>
                <Heading size="md">{t('admin.main.config.llmTokenUsage')}</Heading>
              </HStack>
              <Button size="sm" variant="outline" onClick={handleRefreshAIUsageStats}>
                <FiRefreshCw />
                {t('admin.main.common.refresh')}
              </Button>
            </HStack>
          </Card.Header>
          <Card.Body>
            <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
              <VStack align="start" gap={1} p={3} bg="gray.50" borderRadius="md">
                <Text fontSize="xs" color="gray.600">{t('admin.main.config.usage.totalRequests')}</Text>
                <Text fontWeight="bold" fontSize="lg">{aiUsageStats.total_requests?.toLocaleString?.() ?? aiUsageStats.total_requests}</Text>
              </VStack>

              <VStack align="start" gap={1} p={3} bg="gray.50" borderRadius="md">
                <Text fontSize="xs" color="gray.600">{t('admin.main.config.usage.totalTokens')}</Text>
                <Text fontWeight="bold" fontSize="lg">{aiUsageStats.total_tokens?.toLocaleString?.() ?? aiUsageStats.total_tokens}</Text>
              </VStack>

              <VStack align="start" gap={1} p={3} bg="gray.50" borderRadius="md">
                <Text fontSize="xs" color="gray.600">{t('admin.main.config.usage.avgTokensPerRequest')}</Text>
                <Text fontWeight="bold" fontSize="lg">{aiUsageStats.avg_tokens_per_request}</Text>
              </VStack>

              <VStack align="start" gap={1} p={3} bg="gray.50" borderRadius="md">
                <Text fontSize="xs" color="gray.600">{t('admin.main.config.usage.promptTokens')}</Text>
                <Text fontWeight="bold" fontSize="lg">{aiUsageStats.total_prompt_tokens?.toLocaleString?.() ?? aiUsageStats.total_prompt_tokens}</Text>
              </VStack>

              <VStack align="start" gap={1} p={3} bg="gray.50" borderRadius="md">
                <Text fontSize="xs" color="gray.600">{t('admin.main.config.usage.completionTokens')}</Text>
                <Text fontWeight="bold" fontSize="lg">{aiUsageStats.total_completion_tokens?.toLocaleString?.() ?? aiUsageStats.total_completion_tokens}</Text>
              </VStack>

              <VStack align="start" gap={1} p={3} bg="gray.50" borderRadius="md">
                <Text fontSize="xs" color="gray.600">{t('admin.main.config.usage.cacheHitRate')}</Text>
                <Text fontWeight="bold" fontSize="lg">{aiUsageStats.cache_hit_rate_percent}%</Text>
              </VStack>
            </SimpleGrid>

            <HStack mt={4} gap={6} wrap="wrap">
              <Text fontSize="sm" color="gray.600">
                <strong>{t('admin.main.config.usage.lastModel')}:</strong> {aiUsageStats.last_model || t('admin.main.common.notApplicable')}
              </Text>
              <Text fontSize="sm" color="gray.600">
                <strong>{t('admin.main.config.usage.lastUpdated')}:</strong> {aiUsageStats.last_updated_at ? new Date(aiUsageStats.last_updated_at).toLocaleString() : t('admin.main.common.notApplicable')}
              </Text>
            </HStack>
          </Card.Body>
        </Card.Root>

        {/* System Settings */}
        <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
          <Card.Header bg="orange.50" borderBottom="1px" borderColor={borderColor}>
            <HStack>
              <Icon color="orange.600">
                <FiSettings />
              </Icon>
              <Heading size="md">{t('admin.main.config.systemSettings')}</Heading>
            </HStack>
          </Card.Header>
          <Card.Body>
            <VStack gap={4} align="stretch">
              <HStack justify="space-between" p={3} bg="gray.50" borderRadius="md">
                <VStack align="start" gap={1}>
                  <Text fontWeight="medium">{t('admin.main.config.maintenanceMode')}</Text>
                  <Text fontSize="sm" color="gray.600">{t('admin.main.config.maintenanceModeHelp')}</Text>
                </VStack>
                <Button 
                  size="sm"
                  colorScheme={systemSettings.maintenanceMode ? 'red' : 'gray'}
                  onClick={() => setSystemSettings({...systemSettings, maintenanceMode: !systemSettings.maintenanceMode})}
                >
                  {systemSettings.maintenanceMode ? t('admin.main.status.enabled') : t('admin.main.status.disabled')}
                </Button>
              </HStack>

              <HStack justify="space-between" p={3} bg="gray.50" borderRadius="md">
                <VStack align="start" gap={1}>
                  <Text fontWeight="medium">{t('admin.main.config.allowNewSignups')}</Text>
                  <Text fontSize="sm" color="gray.600">{t('admin.main.config.allowNewSignupsHelp')}</Text>
                </VStack>
                <Button 
                  size="sm"
                  colorScheme={systemSettings.allowSignups ? 'green' : 'red'}
                  onClick={() => setSystemSettings({...systemSettings, allowSignups: !systemSettings.allowSignups})}
                >
                  {systemSettings.allowSignups ? t('admin.main.status.enabled') : t('admin.main.status.disabled')}
                </Button>
              </HStack>

              <HStack justify="space-between" p={3} bg="gray.50" borderRadius="md">
                <VStack align="start" gap={1}>
                  <Text fontWeight="medium">{t('admin.main.config.emailVerificationRequired')}</Text>
                  <Text fontSize="sm" color="gray.600">{t('admin.main.config.emailVerificationRequiredHelp')}</Text>
                </VStack>
                <Button 
                  size="sm"
                  colorScheme={systemSettings.requireEmailVerification ? 'blue' : 'gray'}
                  onClick={() => setSystemSettings({...systemSettings, requireEmailVerification: !systemSettings.requireEmailVerification})}
                >
                  {systemSettings.requireEmailVerification ? t('admin.main.status.required') : t('admin.main.status.optional')}
                </Button>
              </HStack>

              <HStack justify="flex-end" mt={2}>
                <Button 
                  colorScheme="orange" 
                  onClick={handleSaveSystemSettings}
                  loading={isSaving}
                >
                  {t('admin.main.config.saveSystemSettings')}
                </Button>
              </HStack>
            </VStack>
          </Card.Body>
        </Card.Root>

        {/* Current Status - Read Only */}
        <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
          <Card.Header>
            <Heading size="md">{t('admin.main.config.currentSystemStatus')}</Heading>
          </Card.Header>
          <Card.Body>
            <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
              <VStack align="start" gap={2}>
                <Text fontSize="xs" color="gray.600" fontWeight="medium">{t('admin.main.config.environment')}</Text>
                <Badge colorScheme={envInfo?.environment === 'production' ? 'red' : 'blue'} size="lg">
                  {envInfo?.environment ? t(`admin.main.status.${envInfo.environment}`, { defaultValue: envInfo.environment.toUpperCase() }) : t('admin.main.status.unknown').toUpperCase()}
                </Badge>
              </VStack>
              <VStack align="start" gap={2}>
                <Text fontSize="xs" color="gray.600" fontWeight="medium">{t('admin.main.config.version')}</Text>
                <Text fontSize="md" fontWeight="bold">{envInfo?.version || t('admin.main.common.notApplicable')}</Text>
              </VStack>
              <VStack align="start" gap={2}>
                <Text fontSize="xs" color="gray.600" fontWeight="medium">{t('admin.main.config.debugMode')}</Text>
                <Badge colorScheme={appSettings?.debug_mode ? 'orange' : 'gray'}>
                  {appSettings?.debug_mode ? t('admin.main.status.enabled') : t('admin.main.status.disabled')}
                </Badge>
              </VStack>
            </SimpleGrid>
          </Card.Body>
        </Card.Root>
      </VStack>

      {/* Dialog for creating new API key */}
      <DialogRoot open={showNewKeyDialog} onOpenChange={(e) => setShowNewKeyDialog(e.open)} placement="center" scrollBehavior="inside">
        <DialogBackdrop />
        <DialogContent maxW="600px">
          <DialogHeader>{t('admin.main.config.generateNewApiKeyTitle')}</DialogHeader>
          <DialogCloseTrigger />
          <DialogBody>
            <VStack gap={4} align="stretch">
              <Field.Root>
                <Field.Label>{t('admin.main.config.keyName')}</Field.Label>
                <Input 
                  placeholder={t('admin.main.config.keyNamePlaceholder')}
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                />
                <Field.HelperText>{t('admin.main.config.keyNameHelp')}</Field.HelperText>
              </Field.Root>
            </VStack>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewKeyDialog(false)}>
              {t('admin.main.common.cancel')}
            </Button>
            <Button 
              colorScheme="green" 
              onClick={handleGenerateApiKey}
              loading={isSaving}
            >
              {t('admin.main.config.generateKey')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>

      {/* Dialog for showing generated API key */}
      {generatedKeyData && (
        <DialogRoot open={true} onOpenChange={() => setGeneratedKeyData(null)} placement="center" scrollBehavior="inside">
          <DialogBackdrop />
          <DialogContent maxW="600px">
            <DialogHeader>{t('admin.main.config.saveYourApiKeyTitle')}</DialogHeader>
            <DialogCloseTrigger />
            <DialogBody>
              <VStack gap={4} align="stretch">
                <Box p={4} bg="yellow.50" borderRadius="md" border="1px" borderColor="yellow.200">
                  <Text fontSize="sm" fontWeight="bold" color="yellow.800">
                    {generatedKeyData.warning}
                  </Text>
                </Box>

                <Field.Root>
                  <Field.Label>{t('admin.main.config.apiKeyName')}</Field.Label>
                  <Text fontWeight="medium">{generatedKeyData.api_key.name}</Text>
                </Field.Root>

                <Field.Root>
                  <Field.Label>{t('admin.main.config.yourApiKey')}</Field.Label>
                  <HStack>
                    <Input 
                      value={generatedKeyData.full_key}
                      readOnly
                      fontFamily="mono"
                      fontSize="sm"
                    />
                    <Button 
                      onClick={() => {
                        navigator.clipboard.writeText(generatedKeyData.full_key);
                        toaster.success({ title: t('admin.main.config.toasts.copiedToClipboard') });
                      }}
                    >
                      {t('admin.main.common.copy')}
                    </Button>
                  </HStack>
                  <Field.HelperText>
                    {t('admin.main.config.copyKeyHelp')}
                  </Field.HelperText>
                </Field.Root>

                <Box p={4} bg="blue.50" borderRadius="md">
                  <Text fontSize="sm" color="blue.900">
                    <strong>{t('admin.main.config.howToUse')}:</strong> {t('admin.main.config.authorizationHeaderHelp')}
                  </Text>
                  <Box mt={2} p={2} bg="white" borderRadius="md" fontFamily="mono" fontSize="xs">
                    {t('admin.main.config.authorizationBearer', { key: generatedKeyData.full_key })}
                  </Box>
                </Box>
              </VStack>
            </DialogBody>
            <DialogFooter>
              <Button 
                colorScheme="green" 
                onClick={() => setGeneratedKeyData(null)}
              >
                {t('admin.main.config.savedMyKey')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </DialogRoot>
      )}
    </Box>
  );
};

// Subscription Plan Management Component
const SubscriptionPlanManagement = () => {
  const { t } = useTranslation();
  const { data: subscriptionPlans, isLoading: plansLoading } = useSubscriptionPlans();
  const deletePlanMutation = useDeleteSubscriptionPlan();
  const createPlanMutation = useCreateSubscriptionPlan();
  const updatePlanMutation = useUpdateSubscriptionPlan();
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [viewingPlan, setViewingPlan] = useState<any>(null);
  const [newPlan, setNewPlan] = useState({
    name: '',
    description: '',
    tier: '',
    monthly_price: 0,
    annual_price: 0,
    reviews_per_month: 10,
    max_document_size_mb: 100,
    max_documents_per_review: 10,
    report_storage_days: 30,
    api_rate_limit: 1000,
    features: {},
    is_active: true,
  });

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  if (plansLoading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="xl" />
        <Text mt={4}>{t('admin.main.subscriptionPlans.loading')}</Text>
      </Box>
    );
  }

  const handleViewPlan = (plan: any, readOnly: boolean = false) => {
    setViewingPlan(plan);
    // Convert prices from cents to dollars for display
    setNewPlan({
      ...plan,
      monthly_price: plan.monthly_price / 100,
      annual_price: plan.annual_price / 100,
    });
    setIsReadOnly(readOnly);
    setIsViewModalOpen(true);
  };

  const handleEditPlan = (plan: any) => {
    handleViewPlan(plan, false);
  };

  const handleDeletePlan = async (plan: any) => {
    if (window.confirm(t('admin.main.subscriptionPlans.confirmDeletePlan', { name: plan.name }))) {
      try {
        await deletePlanMutation.mutateAsync(plan.id);
        toaster.create({
          title: t('admin.main.subscriptionPlans.toasts.planDeleted'),
          type: 'success',
        });
      } catch (error) {
        toaster.create({
          title: t('admin.main.subscriptionPlans.toasts.errorDeletingPlan'),
          description: t('admin.main.subscriptionPlans.toasts.failedDeletePlan'),
          type: 'error',
        });
      }
    }
  };

  const handleSavePlan = async () => {
    try {
      // Convert prices from dollars to cents for storage
      const planData = {
        ...newPlan,
        monthly_price: Math.round(newPlan.monthly_price * 100),
        annual_price: Math.round(newPlan.annual_price * 100),
      };

      if (viewingPlan) {
        await updatePlanMutation.mutateAsync({ planId: viewingPlan.id, planData });
        toaster.create({
          title: t('admin.main.subscriptionPlans.toasts.planUpdated'),
          type: 'success',
        });
      } else {
        await createPlanMutation.mutateAsync(planData);
        toaster.create({
          title: t('admin.main.subscriptionPlans.toasts.planCreated'),
          type: 'success',
        });
      }
      setIsModalOpen(false);
      setIsViewModalOpen(false);
      setIsReadOnly(false);
      setViewingPlan(null);
      setNewPlan({
        name: '',
        description: '',
        tier: '',
        monthly_price: 0,
        annual_price: 0,
        reviews_per_month: 10,
        max_document_size_mb: 100,
        max_documents_per_review: 10,
        report_storage_days: 30,
        api_rate_limit: 1000,
        features: {},
        is_active: true,
      });
    } catch (error) {
      toaster.create({
        title: t('admin.main.subscriptionPlans.toasts.errorSavingPlan'),
        description: t('admin.main.subscriptionPlans.toasts.failedSavePlan'),
        type: 'error',
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNewPlan(prev => ({ ...prev, [name]: value }));
  };

  const handleIsActiveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewPlan(prev => ({ ...prev, is_active: e.target.checked }));
  };

  return (
    <Box p={6}>
      <VStack gap={6} align="stretch">
        <Flex justify="space-between" align="center">
          <Box>
            <Heading size="lg">{t('admin.main.subscriptionPlans.title')}</Heading>
            <Text color="gray.600">{t('admin.main.subscriptionPlans.subtitle')}</Text>
          </Box>
          <Button colorScheme="blue" onClick={() => setIsModalOpen(true)}>
            <FiPlus />
            {t('admin.main.subscriptionPlans.createNewPlan')}
          </Button>
        </Flex>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={6}>
          {subscriptionPlans?.plans?.map((plan: any, index: number) => (
            <Card.Root 
              key={index} 
              bg={cardBg} 
              border="2px" 
              borderColor={plan.is_active ? 'green.400' : 'gray.300'}
              shadow="md"
              _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
              transition="all 0.2s"
            >
              <Card.Header bg={plan.is_active ? 'green.50' : 'gray.50'}>
                <HStack justify="space-between">
                  <VStack align="start" gap={1}>
                    <Text fontSize="lg" fontWeight="bold" color={plan.is_active ? 'green.700' : 'gray.600'}>
                      {plan.name}
                    </Text>
                    <Text fontSize="sm" fontWeight="semibold" color={plan.is_active ? 'green.600' : 'gray.500'}>
                      {t('admin.main.subscriptionPlans.pricePerMonth', { price: plan.monthly_price / 100 })}
                    </Text>
                  </VStack>
                  <Badge 
                    colorScheme={plan.is_active ? 'green' : 'gray'}
                    size="lg"
                    px={3}
                    py={1}
                    borderRadius="full"
                  >
                    {plan.is_active ? t('admin.main.status.active') : t('admin.main.status.inactive')}
                  </Badge>
                </HStack>
              </Card.Header>
              <Card.Body>
                <VStack gap={4} align="stretch">
                  <Text fontSize="sm" color="gray.600">{plan.description}</Text>
                  
                  <VStack gap={2} align="stretch">
                    <HStack justify="space-between">
                      <Text fontSize="sm" fontWeight="medium">{t('admin.main.subscriptionPlans.planCode')}</Text>
                      <Badge colorScheme="cyan" variant="solid" borderRadius="md" px={2}>
                        {plan.tier || t('admin.main.common.notSet')}
                      </Badge>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="sm" fontWeight="medium">{t('admin.main.subscriptionPlans.reviewsPerMonth')}</Text>
                      <Badge colorScheme="blue" variant="solid" borderRadius="md" px={2}>
                        {plan.reviews_per_month === -1 ? t('admin.main.common.unlimited') : plan.reviews_per_month}
                      </Badge>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="sm" fontWeight="medium">{t('admin.main.subscriptionPlans.maxFileSize')}</Text>
                      <Badge colorScheme="green" variant="solid" borderRadius="md" px={2}>
                        {plan.max_document_size_mb}MB
                      </Badge>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="sm" fontWeight="medium">{t('admin.main.subscriptionPlans.reportStorage')}</Text>
                      <Badge colorScheme="purple" variant="solid" borderRadius="md" px={2}>
                        {t('admin.main.subscriptionPlans.daysCount', { count: plan.report_storage_days })}
                      </Badge>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="sm" fontWeight="medium">{t('admin.main.subscriptionPlans.apiRateLimit')}</Text>
                      <Badge colorScheme="orange" variant="solid" borderRadius="md" px={2}>
                        {t('admin.main.subscriptionPlans.requestsPerHour', { count: plan.api_rate_limit || 1000 })}
                      </Badge>
                    </HStack>
                  </VStack>

                  <HStack gap={2} mt={2}>
                    <Button 
                      size="sm" 
                      variant="solid" 
                      colorScheme="blue" 
                      onClick={() => handleEditPlan(plan)}
                      flex={1}
                    >
                      <FiEdit />
                      {t('admin.main.common.edit')}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      colorScheme="teal" 
                      onClick={() => handleViewPlan(plan, true)}
                      flex={1}
                    >
                      <FiEye />
                      {t('admin.main.common.view')}
                    </Button>
                    <IconButton 
                      size="sm" 
                      variant="outline" 
                      colorScheme="red" 
                      onClick={() => handleDeletePlan(plan)}
                      aria-label={t('admin.main.subscriptionPlans.deletePlan')}
                    >
                      <FiTrash2 />
                    </IconButton>
                  </HStack>
                </VStack>
              </Card.Body>
            </Card.Root>
          ))}
        </SimpleGrid>

        {(!subscriptionPlans?.plans || subscriptionPlans.plans.length === 0) && (
          <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
            <Card.Body textAlign="center" py={12}>
              <VStack gap={4}>
                <Icon as={FiCreditCard} boxSize={12} color="gray.400" />
                <Text fontSize="lg" fontWeight="medium" color="gray.600">
                  {t('admin.main.subscriptionPlans.noPlans')}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  {t('admin.main.subscriptionPlans.createFirstPlanHelp')}
                </Text>
                <Button colorScheme="blue" onClick={() => setIsModalOpen(true)}>
                  <FiPlus />
                  {t('admin.main.subscriptionPlans.createFirstPlan')}
                </Button>
              </VStack>
            </Card.Body>
          </Card.Root>
        )}
      </VStack>

      {/* Edit/Create Plan Modal */}
      <DialogRoot 
        open={isModalOpen || isViewModalOpen} 
        onOpenChange={(e) => { setIsModalOpen(e.open); setIsViewModalOpen(e.open); }} 
        size="xl"
        placement="center"
        scrollBehavior="inside"
      >
        <DialogBackdrop />
        <DialogContent 
          maxH="90vh" 
          position="fixed"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
        >
          <DialogHeader>
            {isReadOnly ? t('admin.main.subscriptionPlans.viewPlanTitle') : (viewingPlan ? t('admin.main.subscriptionPlans.editPlanTitle') : t('admin.main.subscriptionPlans.createNewPlan'))}
          </DialogHeader>
          <DialogCloseTrigger />
          <DialogBody maxH="calc(90vh - 180px)" overflowY="auto">
            <VStack gap={4} align="stretch">
              <Field.Root>
                <Field.Label>{t('admin.main.subscriptionPlans.planName')}</Field.Label>
                <Input 
                  name="name" 
                  value={newPlan.name} 
                  onChange={handleInputChange} 
                  placeholder={t('admin.main.subscriptionPlans.planNamePlaceholder')} 
                  readOnly={isReadOnly}
                  disabled={isReadOnly}
                />
              </Field.Root>
              <Field.Root>
                <Field.Label>{t('admin.main.common.description')}</Field.Label>
                <Input 
                  name="description" 
                  value={newPlan.description} 
                  onChange={handleInputChange} 
                  placeholder={t('admin.main.subscriptionPlans.descriptionPlaceholder')} 
                  readOnly={isReadOnly}
                  disabled={isReadOnly}
                />
              </Field.Root>
              <Field.Root>
                <Field.Label>{t('admin.main.subscriptionPlans.planCodeField')}</Field.Label>
                <Input
                  name="tier"
                  value={newPlan.tier}
                  onChange={handleInputChange}
                  placeholder={t('admin.main.subscriptionPlans.planCodePlaceholder')}
                  readOnly={isReadOnly || !!viewingPlan}
                  disabled={isReadOnly}
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {viewingPlan
                    ? t('admin.main.subscriptionPlans.planCodeLockedHelp')
                    : t('admin.main.subscriptionPlans.planCodeHelp')}
                </Text>
              </Field.Root>
              <Field.Root>
                <Field.Label>{t('admin.main.subscriptionPlans.monthlyPrice')}</Field.Label>
                <NumberInput.Root 
                  value={String(newPlan.monthly_price)} 
                  onValueChange={(e) => setNewPlan(prev => ({ ...prev, monthly_price: Number(e.value) }))}
                  min={0}
                  disabled={isReadOnly}
                >
                  <NumberInput.Input />
                  <NumberInput.Control>
                    <NumberInput.IncrementTrigger />
                    <NumberInput.DecrementTrigger />
                  </NumberInput.Control>
                </NumberInput.Root>
              </Field.Root>
              <Field.Root>
                <Field.Label>{t('admin.main.subscriptionPlans.annualPrice')}</Field.Label>
                <NumberInput.Root 
                  value={String(newPlan.annual_price)} 
                  onValueChange={(e) => setNewPlan(prev => ({ ...prev, annual_price: Number(e.value) }))}
                  min={0}
                  disabled={isReadOnly}
                >
                  <NumberInput.Input />
                  <NumberInput.Control>
                    <NumberInput.IncrementTrigger />
                    <NumberInput.DecrementTrigger />
                  </NumberInput.Control>
                </NumberInput.Root>
              </Field.Root>
              <Field.Root>
                <Field.Label>{t('admin.main.subscriptionPlans.assessmentsPerMonth')}</Field.Label>
                <NumberInput.Root 
                  value={String(newPlan.reviews_per_month)} 
                  onValueChange={(e) => setNewPlan(prev => ({ ...prev, reviews_per_month: Number(e.value) }))}
                  min={-1}
                  disabled={isReadOnly}
                >
                  <NumberInput.Input />
                  <NumberInput.Control>
                    <NumberInput.IncrementTrigger />
                    <NumberInput.DecrementTrigger />
                  </NumberInput.Control>
                </NumberInput.Root>
              </Field.Root>
              <Field.Root>
                <Field.Label>{t('admin.main.subscriptionPlans.maxDocumentSize')}</Field.Label>
                <NumberInput.Root 
                  value={String(newPlan.max_document_size_mb)} 
                  onValueChange={(e) => setNewPlan(prev => ({ ...prev, max_document_size_mb: Number(e.value) }))}
                  min={1}
                  disabled={isReadOnly}
                >
                  <NumberInput.Input />
                  <NumberInput.Control>
                    <NumberInput.IncrementTrigger />
                    <NumberInput.DecrementTrigger />
                  </NumberInput.Control>
                </NumberInput.Root>
              </Field.Root>
              <Field.Root>
                <Field.Label>{t('admin.main.subscriptionPlans.maxDocumentsPerAssessment')}</Field.Label>
                <NumberInput.Root 
                  value={String(newPlan.max_documents_per_review)} 
                  onValueChange={(e) => setNewPlan(prev => ({ ...prev, max_documents_per_review: Number(e.value) }))}
                  min={1}
                  disabled={isReadOnly}
                >
                  <NumberInput.Input />
                  <NumberInput.Control>
                    <NumberInput.IncrementTrigger />
                    <NumberInput.DecrementTrigger />
                  </NumberInput.Control>
                </NumberInput.Root>
              </Field.Root>
              <Field.Root>
                <Field.Label>{t('admin.main.subscriptionPlans.reportStorageDays')}</Field.Label>
                <NumberInput.Root 
                  value={String(newPlan.report_storage_days)} 
                  onValueChange={(e) => setNewPlan(prev => ({ ...prev, report_storage_days: Number(e.value) }))}
                  min={1}
                  disabled={isReadOnly}
                >
                  <NumberInput.Input />
                  <NumberInput.Control>
                    <NumberInput.IncrementTrigger />
                    <NumberInput.DecrementTrigger />
                  </NumberInput.Control>
                </NumberInput.Root>
              </Field.Root>
              <Field.Root>
                <Field.Label>{t('admin.main.subscriptionPlans.apiRateLimitRequestsHour')}</Field.Label>
                <NumberInput.Root 
                  value={String(newPlan.api_rate_limit)} 
                  onValueChange={(e) => setNewPlan(prev => ({ ...prev, api_rate_limit: Number(e.value) }))}
                  min={1}
                  disabled={isReadOnly}
                >
                  <NumberInput.Input />
                  <NumberInput.Control>
                    <NumberInput.IncrementTrigger />
                    <NumberInput.DecrementTrigger />
                  </NumberInput.Control>
                </NumberInput.Root>
                <Field.HelperText>{t('admin.main.subscriptionPlans.apiRateLimitHelp')}</Field.HelperText>
              </Field.Root>
              <Field.Root>
                <Field.Label>{t('admin.main.subscriptionPlans.featuresJson')}</Field.Label>
                <Input 
                  value={JSON.stringify(newPlan.features, null, 2)} 
                  onChange={(e) => {
                    try {
                      const features = JSON.parse(e.target.value);
                      setNewPlan(prev => ({ ...prev, features }));
                    } catch (error) {
                      // If JSON is invalid, just store the raw string
                      setNewPlan(prev => ({ ...prev, features: e.target.value }));
                    }
                  }}
                  placeholder='{"feature1": "value1", "feature2": "value2"}' 
                  readOnly={isReadOnly}
                  disabled={isReadOnly}
                />
              </Field.Root>
              <Field.Root display="flex" alignItems="center">
                <Field.Label mb="0">{t('admin.main.subscriptionPlans.isActive')}</Field.Label>
                <input 
                    placeholder="llama3.2"
                  name="is_active" 
                  checked={newPlan.is_active} 
                  onChange={handleIsActiveChange} 
                  disabled={isReadOnly}
                  style={{ marginLeft: '10px' }} 
                />
              </Field.Root>
            </VStack>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setIsModalOpen(false); setIsViewModalOpen(false); setIsReadOnly(false); setViewingPlan(null); }}>
              {isReadOnly ? t('admin.main.common.close') : t('admin.main.common.cancel')}
            </Button>
            {!isReadOnly && (
              <Button colorScheme="blue" onClick={handleSavePlan}>
                {viewingPlan ? t('admin.main.subscriptionPlans.updatePlan') : t('admin.main.subscriptionPlans.createPlan')}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </Box>
  );
};

// Backup & Recovery Component
const BackupRecoverySection = () => {
  const { t } = useTranslation();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  
  // State
  const [backups, setBackups] = useState<any[]>([]);
  const [statistics, setStatistics] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState<any>(null);
  
  // Create backup form
  const [backupComponents, setBackupComponents] = useState({
    database: true,
    chromadb: true,
    configs: true
  });
  const [backupDescription, setBackupDescription] = useState('');
  
  // Restore form
  const [restoreComponents, setRestoreComponents] = useState<string[]>([]);
  const [restoreForce, setRestoreForce] = useState(false);
  
  // Fetch backups and statistics
  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [backupsRes, statsRes] = await Promise.all([
        adminAPI.listBackups(),
        adminAPI.getBackupStatistics()
      ]);
      setBackups(backupsRes.backups || []);
      setStatistics(statsRes.statistics || {});
    } catch (error) {
      console.error('Failed to load backup data:', error);
      toaster.error({ title: t('admin.main.backup.toasts.failedLoadBackupData') });
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, []);
  
  // Create backup
  const handleCreateBackup = async () => {
    setIsCreating(true);
    try {
      const components = [];
      if (backupComponents.database) components.push('database');
      if (backupComponents.chromadb) components.push('chromadb');
      if (backupComponents.configs) components.push('configs');
      
      if (components.length === 0) {
        toaster.error({ title: t('admin.main.backup.toasts.selectComponent') });
        return;
      }
      
      await adminAPI.createBackup(components, backupDescription);
      toaster.success({ title: t('admin.main.backup.toasts.backupCreated') });
      setShowCreateDialog(false);
      setBackupDescription('');
      setBackupComponents({ database: true, chromadb: true, configs: true });
      await fetchData();
    } catch (error) {
      console.error('Failed to create backup:', error);
      toaster.error({ title: t('admin.main.backup.toasts.failedCreateBackup') });
    } finally {
      setIsCreating(false);
    }
  };
  
  // Download backup
  const handleDownloadBackup = async (backupName: string) => {
    try {
      console.log('Downloading backup:', backupName);
      const blob = await adminAPI.downloadBackup(backupName);
      console.log('Blob received:', blob.size, 'bytes');
      
      if (!blob || blob.size === 0) {
        throw new Error(t('admin.main.backup.toasts.emptyBackupFile'));
      }
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${backupName}.tar.gz`;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      
      // Clean up after a short delay to ensure download starts
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);
      
      toaster.success({ title: t('admin.main.backup.toasts.downloadStarted') });
    } catch (error: any) {
      console.error('Failed to download backup:', error);
      toaster.error({ 
        title: t('admin.main.backup.toasts.failedDownloadBackup'),
        description: error?.message || t('admin.main.status.unknownErrorOccurred')
      });
    }
  };
  
  // Delete backup
  const handleDeleteBackup = async (backupName: string) => {
    if (!confirm(t('admin.main.backup.confirmDeleteBackup', { name: backupName }))) {
      return;
    }
    
    try {
      await adminAPI.deleteBackup(backupName);
      toaster.success({ title: t('admin.main.backup.toasts.backupDeleted') });
      await fetchData();
    } catch (error) {
      console.error('Failed to delete backup:', error);
      toaster.error({ title: t('admin.main.backup.toasts.failedDeleteBackup') });
    }
  };
  
  // Restore backup
  const handleRestoreBackup = async () => {
    if (!restoreForce) {
      toaster.error({ 
        title: t('admin.main.backup.toasts.pleaseConfirm'), 
        description: t('admin.main.backup.toasts.mustConfirmRestore') 
      });
      return;
    }
    
    if (!confirm(t('admin.main.backup.confirmOverwriteRestore'))) {
      return;
    }
    
    try {
      const components = restoreComponents.length > 0 ? restoreComponents : undefined;
      await adminAPI.restoreBackup(selectedBackup.backup_name, components, restoreForce);
      toaster.success({ 
        title: t('admin.main.backup.toasts.backupRestored'),
        description: t('admin.main.backup.toasts.restartMayBeNeeded')
      });
      setShowRestoreDialog(false);
      setSelectedBackup(null);
      setRestoreComponents([]);
      setRestoreForce(false);
    } catch (error: any) {
      console.error('Failed to restore backup:', error);
      toaster.error({ 
        title: t('admin.main.backup.toasts.failedRestoreBackup'),
        description: error.response?.data?.detail || error.message
      });
    }
  };
  
  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };
  
  // Format size
  const formatSize = (bytes: number) => {
    if (!bytes) return t('admin.main.common.notApplicable');
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };
  
  if (isLoading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="xl" />
        <Text mt={4}>{t('admin.main.backup.loading')}</Text>
      </Box>
    );
  }
  
  return (
    <Box p={6}>
      <VStack gap={6} align="stretch">
        <HStack justify="space-between">
          <Heading size="lg">{t('admin.main.backup.title')}</Heading>
          <Button 
            colorScheme="blue" 
            onClick={() => setShowCreateDialog(true)}
          >
            <FiDownload />
            {t('admin.main.backup.createBackup')}
          </Button>
        </HStack>
        
        {/* Statistics Cards */}
        <SimpleGrid columns={{ base: 1, md: 3 }} gap={6}>
          <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
            <Card.Header>
              <Heading size="sm">{t('admin.main.backup.totalBackups')}</Heading>
            </Card.Header>
            <Card.Body>
              <Text fontSize="3xl" fontWeight="bold" color="blue.500">
                {statistics?.total_backups || 0}
              </Text>
              <Text fontSize="sm" color="gray.600">
                {t('admin.main.backup.successfulCount', { count: statistics?.successful_backups || 0 })}
              </Text>
            </Card.Body>
          </Card.Root>
          
          <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
            <Card.Header>
              <Heading size="sm">{t('admin.main.backup.storageUsed')}</Heading>
            </Card.Header>
            <Card.Body>
              <Text fontSize="3xl" fontWeight="bold" color="green.500">
                {statistics?.total_size_gb || 0} GB
              </Text>
              <Text fontSize="sm" color="gray.600">
                {t('admin.main.backup.totalBackupSize')}
              </Text>
            </Card.Body>
          </Card.Root>
          
          <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
            <Card.Header>
              <Heading size="sm">{t('admin.main.backup.latestBackup')}</Heading>
            </Card.Header>
            <Card.Body>
              {statistics?.latest_backup ? (
                <>
                  <Text fontSize="sm" fontWeight="medium">
                    {formatDate(statistics.latest_backup.timestamp)}
                  </Text>
                  <Badge colorScheme={statistics.latest_backup.status === 'completed' ? 'green' : 'red'} mt={2}>
                    {t(`admin.main.status.${statistics.latest_backup.status}`, { defaultValue: statistics.latest_backup.status })}
                  </Badge>
                </>
              ) : (
                <Text fontSize="sm" color="gray.600">{t('admin.main.backup.noBackupsYet')}</Text>
              )}
            </Card.Body>
          </Card.Root>
        </SimpleGrid>
        
        {/* Backup History Table */}
        <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
          <Card.Header>
            <HStack justify="space-between">
              <Heading size="md">{t('admin.main.backup.backupHistory')}</Heading>
              <Button size="sm" onClick={fetchData}>
                <FiRefreshCw />
                {t('admin.main.common.refresh')}
              </Button>
            </HStack>
          </Card.Header>
          <Card.Body>
            {backups.length === 0 ? (
              <Text textAlign="center" color="gray.600" py={8}>
                {t('admin.main.backup.noBackupsAvailable')}
              </Text>
            ) : (
              <Table.Root variant="line" size="sm">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>{t('admin.main.backup.timestamp')}</Table.ColumnHeader>
                    <Table.ColumnHeader>{t('admin.main.backup.components')}</Table.ColumnHeader>
                    <Table.ColumnHeader>{t('admin.main.backup.size')}</Table.ColumnHeader>
                    <Table.ColumnHeader>{t('admin.main.common.status')}</Table.ColumnHeader>
                    <Table.ColumnHeader>{t('admin.main.common.description')}</Table.ColumnHeader>
                    <Table.ColumnHeader>{t('admin.main.common.actions')}</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {backups.map((backup) => (
                    <Table.Row key={backup.backup_name}>
                      <Table.Cell>{formatDate(backup.timestamp)}</Table.Cell>
                      <Table.Cell>
                        <HStack gap={1}>
                          {backup.components.map((comp: string) => (
                            <Badge key={comp} size="sm" variant="subtle">
                              {t(`admin.main.backup.componentNames.${comp}`, { defaultValue: comp })}
                            </Badge>
                          ))}
                        </HStack>
                      </Table.Cell>
                      <Table.Cell>{formatSize(backup.total_size)}</Table.Cell>
                      <Table.Cell>
                        <Badge colorScheme={backup.status === 'completed' ? 'green' : backup.status === 'failed' ? 'red' : 'yellow'}>
                          {t(`admin.main.status.${backup.status}`, { defaultValue: backup.status })}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>
                        <Text fontSize="sm" lineClamp={1}>
                          {backup.description || '-'}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <HStack gap={1}>
                          <IconButton 
                            size="sm" 
                            aria-label={t('admin.main.backup.restore')} 
                            colorScheme="green"
                            onClick={() => {
                              setSelectedBackup(backup);
                              setRestoreComponents(backup.components);
                              setShowRestoreDialog(true);
                            }}
                            disabled={backup.status !== 'completed'}
                          >
                            <FiUpload />
                          </IconButton>
                          <IconButton 
                            size="sm" 
                            aria-label={t('admin.main.common.download')}
                            colorScheme="blue"
                            onClick={() => handleDownloadBackup(backup.backup_name)}
                            disabled={backup.status !== 'completed'}
                          >
                            <FiDownload />
                          </IconButton>
                          <IconButton 
                            size="sm" 
                            aria-label={t('admin.main.common.delete')}
                            colorScheme="red"
                            onClick={() => handleDeleteBackup(backup.backup_name)}
                          >
                            <FiTrash2 />
                          </IconButton>
                        </HStack>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            )}
          </Card.Body>
        </Card.Root>
      </VStack>
      
      {/* {t('admin.main.backup.createBackup')} Dialog */}
      <DialogRoot open={showCreateDialog} onOpenChange={(e) => setShowCreateDialog(e.open)} placement="center" scrollBehavior="inside">
        <DialogBackdrop />
        <DialogContent maxW="600px">
          <DialogHeader>
            <DialogTitle>{t('admin.main.backup.createNewBackup')}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <VStack gap={4} align="stretch">
              <Field.Root>
                <Field.Label>{t('admin.main.backup.descriptionOptional')}</Field.Label>
                <Input 
                  placeholder={t('admin.main.backup.descriptionPlaceholder')}
                  value={backupDescription}
                  onChange={(e) => setBackupDescription(e.target.value)}
                />
              </Field.Root>
              
              <Field.Root>
                <Field.Label>{t('admin.main.backup.componentsToBackup')}</Field.Label>
                <VStack gap={2} align="stretch">
                  <HStack>
                    <input 
                      type="checkbox" 
                      checked={backupComponents.database}
                      onChange={(e) => setBackupComponents({...backupComponents, database: e.target.checked})}
                    />
                    <Text>{t('admin.main.backup.postgresqlDatabase')}</Text>
                  </HStack>
                  <HStack>
                    <input 
                      type="checkbox" 
                      checked={backupComponents.chromadb}
                      onChange={(e) => setBackupComponents({...backupComponents, chromadb: e.target.checked})}
                    />
                    <Text>{t('admin.main.backup.chromadbVectorStore')}</Text>
                  </HStack>
                  <HStack>
                    <input 
                      type="checkbox" 
                      checked={backupComponents.configs}
                      onChange={(e) => setBackupComponents({...backupComponents, configs: e.target.checked})}
                    />
                    <Text>{t('admin.main.backup.configurationFiles')}</Text>
                  </HStack>
                </VStack>
              </Field.Root>
            </VStack>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              {t('admin.main.common.cancel')}
            </Button>
            <Button colorScheme="blue" onClick={handleCreateBackup} loading={isCreating}>
              {t('admin.main.backup.createBackup')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
      
      {/* {t('admin.main.backup.restoreBackup')} Dialog */}
      <DialogRoot open={showRestoreDialog} onOpenChange={(e) => setShowRestoreDialog(e.open)} placement="center" scrollBehavior="inside">
        <DialogBackdrop />
        <DialogContent 
          maxW="600px" 
          maxH="90vh"
          position="fixed"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
        >
          <DialogHeader>
            <DialogTitle>{t('admin.main.backup.restoreFromBackup')}</DialogTitle>
          </DialogHeader>
          <DialogBody maxH="calc(90vh - 180px)" overflowY="auto">
            <VStack gap={4} align="stretch">
              <Box p={3} bg="orange.50" borderRadius="md" border="1px" borderColor="orange.200">
                <HStack gap={2}>
                  <Icon color="orange.600">
                    <FiAlertTriangle />
                  </Icon>
                  <VStack align="start" gap={0}>
                    <Text fontWeight="bold" fontSize="sm" color="orange.800">{t('admin.main.common.warning')}</Text>
                    <Text fontSize="xs" color="orange.700">
                      {t('admin.main.backup.overwriteWarning')}
                    </Text>
                  </VStack>
                </HStack>
              </Box>
              
              {selectedBackup && (
                <>
                  <Field.Root>
                    <Field.Label>{t('admin.main.backup.backupDetails')}</Field.Label>
                    <VStack align="start" gap={1} p={3} bg="gray.50" borderRadius="md">
                      <Text fontSize="sm"><strong>{t('admin.main.common.name')}:</strong> {selectedBackup.backup_name}</Text>
                      <Text fontSize="sm"><strong>{t('admin.main.common.date')}:</strong> {formatDate(selectedBackup.timestamp)}</Text>
                      <Text fontSize="sm"><strong>{t('admin.main.backup.size')}:</strong> {formatSize(selectedBackup.total_size)}</Text>
                    </VStack>
                  </Field.Root>
                  
                  <Field.Root>
                    <Field.Label>{t('admin.main.backup.componentsToRestore')}</Field.Label>
                    <VStack gap={2} align="stretch">
                      {selectedBackup.components.map((comp: string) => (
                        <HStack key={comp}>
                          <input
                            type="checkbox"
                            checked={restoreComponents.includes(comp)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setRestoreComponents([...restoreComponents, comp]);
                              } else {
                                setRestoreComponents(restoreComponents.filter(c => c !== comp));
                              }
                            }}
                          />
                          <Text>{t(`admin.main.backup.componentNames.${comp}`, { defaultValue: comp.charAt(0).toUpperCase() + comp.slice(1) })}</Text>
                        </HStack>
                      ))}
                    </VStack>
                    <Field.HelperText>
                      {t('admin.main.backup.restoreAllHelp')}
                    </Field.HelperText>
                  </Field.Root>
                  
                  <HStack>
                    <input
                      type="checkbox"
                      checked={restoreForce}
                      onChange={(e) => setRestoreForce(e.target.checked)}
                      style={{ accentColor: 'red' }}
                    />
                    <Text fontWeight="bold" color="red.600">{t('admin.main.backup.understandOverwrite')}</Text>
                  </HStack>
                </>
              )}
            </VStack>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowRestoreDialog(false);
              setSelectedBackup(null);
              setRestoreComponents([]);
              setRestoreForce(false);
            }}>
              {t('admin.main.common.cancel')}
            </Button>
            <Button colorScheme="red" onClick={handleRestoreBackup} disabled={!restoreForce}>
              {t('admin.main.backup.restoreBackup')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </Box>
  );
};

// --- CHATWOOT SUPPORT CENTER ---
const ChatwootSupportSection = () => {
  const { t } = useTranslation();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const [chatwootConfig, setChatwootConfig] = useState({
    baseUrl: '',
    websiteToken: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await adminAPI.getChatwootSettings();
        setChatwootConfig({
          baseUrl: settings.chatwoot_base_url || '',
          websiteToken: settings.chatwoot_website_token || ''
        });
      } catch (error) {
        console.error('Failed to fetch Chatwoot settings:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await adminAPI.updateChatwootSettings({
        chatwoot_base_url: chatwootConfig.baseUrl,
        chatwoot_website_token: chatwootConfig.websiteToken
      });
      toaster.success({ title: t('admin.main.support.toasts.chatwootSaved') });
    } catch (error) {
      toaster.error({ title: t('admin.main.support.toasts.chatwootSaveFailed') });
    } finally {
      setIsSaving(false);
    }
  };

  const isConfigured = chatwootConfig.baseUrl && chatwootConfig.websiteToken;

  if (isLoading) {
    return <Box p={8} textAlign="center"><Spinner size="xl" /></Box>;
  }

  return (
    <Box p={6}>
      <VStack gap={6} align="stretch">
        <HStack justify="space-between">
          <VStack align="start" gap={1}>
            <Heading size="lg">{t('admin.main.support.title')}</Heading>
            <Text color="gray.600" fontSize="sm">
              {t('admin.main.support.subtitle')}
            </Text>
          </VStack>
          <Badge colorScheme={isConfigured ? 'green' : 'orange'} fontSize="sm" px={3} py={1}>
            {isConfigured ? t('admin.main.status.connected') : t('admin.main.status.notConfigured')}
          </Badge>
        </HStack>

        <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
          {/* Configuration Card */}
          <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
            <Card.Header>
              <HStack>
                <FiMessageCircle />
                <Heading size="md">{t('admin.main.support.chatwootConfiguration')}</Heading>
              </HStack>
            </Card.Header>
            <Card.Body>
              <VStack gap={4} align="stretch">
                <Field.Root>
                  <Field.Label>{t('admin.main.support.chatwootBaseUrl')}</Field.Label>
                  <Input
                    placeholder="https://app.chatwoot.com"
                    value={chatwootConfig.baseUrl}
                    onChange={(e) => setChatwootConfig({ ...chatwootConfig, baseUrl: e.target.value })}
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    {t('admin.main.support.chatwootBaseUrlHelp')}
                  </Text>
                </Field.Root>

                <Field.Root>
                  <Field.Label>{t('admin.main.support.websiteToken')}</Field.Label>
                  <Input
                    placeholder="your-website-token"
                    value={chatwootConfig.websiteToken}
                    onChange={(e) => setChatwootConfig({ ...chatwootConfig, websiteToken: e.target.value })}
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    {t('admin.main.support.websiteTokenHelp')}
                  </Text>
                </Field.Root>

                <Button
                  colorScheme="blue"
                  onClick={handleSave}
                  loading={isSaving}
                >
                  {t('admin.main.common.saveConfiguration')}
                </Button>
              </VStack>
            </Card.Body>
          </Card.Root>

          {/* Setup Guide Card */}
          <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
            <Card.Header>
              <HStack>
                <FiBookOpen />
                <Heading size="md">{t('admin.main.support.setupGuide')}</Heading>
              </HStack>
            </Card.Header>
            <Card.Body>
              <VStack gap={3} align="stretch">
                <Text fontSize="sm" color="gray.600">
                  {t('admin.main.support.setupDescription')}
                </Text>
                <VStack align="start" gap={2}>
                  <HStack>
                    <Badge colorScheme="blue" fontSize="xs">1</Badge>
                    <Text fontSize="sm">{t('admin.main.support.stepSignUp')}</Text>
                  </HStack>
                  <HStack>
                    <Badge colorScheme="blue" fontSize="xs">2</Badge>
                    <Text fontSize="sm">{t('admin.main.support.stepCreateInbox')}</Text>
                  </HStack>
                  <HStack>
                    <Badge colorScheme="blue" fontSize="xs">3</Badge>
                    <Text fontSize="sm">{t('admin.main.support.stepCopyCredentials')}</Text>
                  </HStack>
                  <HStack>
                    <Badge colorScheme="blue" fontSize="xs">4</Badge>
                    <Text fontSize="sm">{t('admin.main.support.stepPasteSave')}</Text>
                  </HStack>
                </VStack>
                <Button
                  variant="outline"
                  size="sm"
                  mt={2}
                  onClick={() => window.open('https://www.chatwoot.com/docs/product/channels/live-chat/create-website', '_blank')}
                >
                  <FiExternalLink />
                  {t('admin.main.support.chatwootDocumentation')}
                </Button>
                {isConfigured && (
                  <Button
                    variant="outline"
                    colorScheme="green"
                    size="sm"
                    onClick={() => window.open(chatwootConfig.baseUrl, '_blank')}
                  >
                    <FiExternalLink />
                    {t('admin.main.support.openChatwootDashboard')}
                  </Button>
                )}
              </VStack>
            </Card.Body>
          </Card.Root>
        </SimpleGrid>

        {isConfigured && (
          <Card.Root bg="green.50" border="1px" borderColor="green.200">
            <Card.Body>
              <HStack>
                <FiMessageCircle color="#38A169" />
                <Text fontSize="sm" color="green.700">
                  {t('admin.main.support.widgetActive')} 
                  <a href={chatwootConfig.baseUrl} target="_blank" rel="noreferrer" style={{ textDecoration: 'underline', fontWeight: 500 }}>
                    {t('admin.main.support.chatwootDashboard')}
                  </a>.
                </Text>
              </HStack>
            </Card.Body>
          </Card.Root>
        )}
      </VStack>
    </Box>
  );
};


// --- NOTIFICATIONS CENTER ---
const NotificationsSection = () => {
  const { t } = useTranslation();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const [notifications, setNotifications] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [totalAll, setTotalAll] = useState(0);
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const fetchNotifications = async (currentFilter: string, currentPage: number) => {
    setIsLoading(true);
    try {
      const data = await adminAPI.getNotifications({
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
        category: currentFilter,
      });
      setNotifications(data.notifications || []);
      setTotal(data.total || 0);
      setTotalAll(data.total_all || 0);
      setCategoryCounts(data.category_counts || {});
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications(filter, page);
  }, [filter, page]);

  const handleFilterChange = (newFilter: string) => {
    setFilter(newFilter);
    setPage(1);
  };

  const totalPages = Math.ceil(total / pageSize);

  const formatAction = (action: string) => {
    const defaultValue = action
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
    return t(`admin.main.notifications.actions.${action}`, { defaultValue });
  };

  const formatTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('admin.main.notifications.justNow');
    if (diffMins < 60) return t('admin.main.notifications.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('admin.main.notifications.hoursAgo', { count: diffHours });
    if (diffDays < 7) return t('admin.main.notifications.daysAgo', { count: diffDays });
    return date.toLocaleDateString();
  };

  const categoryColors: Record<string, string> = {
    security: 'red',
    user: 'blue',
    system: 'purple',
    api: 'orange',
    activity: 'green',
  };

  return (
    <Box p={6}>
      <VStack gap={6} align="stretch">
        <HStack justify="space-between">
          <VStack align="start" gap={1}>
            <Heading size="lg">{t('admin.main.notifications.title')}</Heading>
            <Text color="gray.600" fontSize="sm">
              {t('admin.main.notifications.systemEventsTracked', { count: totalAll })}
            </Text>
          </VStack>
          <Button variant="outline" size="sm" onClick={() => fetchNotifications(filter, page)}>
            <FiRefreshCw />
            {t('admin.main.common.refresh')}
          </Button>
        </HStack>

        {/* Category Filter */}
        <HStack gap={2} flexWrap="wrap">
          <Button
            size="xs"
            variant={filter === 'all' ? 'solid' : 'outline'}
            colorScheme="gray"
            onClick={() => handleFilterChange('all')}
          >
            {t('admin.main.notifications.filterAll', { count: totalAll })}
          </Button>
          {Object.entries(categoryColors).map(([cat, color]) => (
            <Button
              key={cat}
              size="xs"
              variant={filter === cat ? 'solid' : 'outline'}
              colorScheme={color}
              onClick={() => handleFilterChange(cat)}
            >
              {t(`admin.main.notifications.categories.${cat}`, { defaultValue: cat })} ({categoryCounts[cat] || 0})
            </Button>
          ))}
        </HStack>

        {/* Notifications List */}
        <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
          <Card.Body p={0}>
            {isLoading ? (
              <Box p={8} textAlign="center"><Spinner size="xl" /></Box>
            ) : notifications.length === 0 ? (
              <Box p={8} textAlign="center">
                <Text color="gray.500">{t('admin.main.notifications.noNotificationsFound')}</Text>
              </Box>
            ) : (
              <VStack gap={0} align="stretch" divideY="1px" borderColor={borderColor}>
                {notifications.map((n) => (
                  <HStack key={n.id} px={4} py={3} justify="space-between" _hover={{ bg: hoverBg }}>
                    <HStack gap={3} flex={1} minW={0}>
                      <Badge colorScheme={n.color} fontSize="2xs" px={2} py={0.5} borderRadius="full">
                        {t(`admin.main.notifications.categories.${n.category}`, { defaultValue: n.category })}
                      </Badge>
                      <VStack align="start" gap={0} minW={0} flex={1}>
                        <Text fontSize="sm" fontWeight="medium" truncate>
                          {formatAction(n.action)}
                        </Text>
                        <Text fontSize="xs" color="gray.500" truncate>
                          {n.username}
                          {n.ip_address && ` • ${n.ip_address}`}
                        </Text>
                      </VStack>
                    </HStack>
                    <HStack gap={2} flexShrink={0}>
                      {!n.success && (
                        <Badge colorScheme="red" fontSize="2xs">{t('admin.main.status.failed')}</Badge>
                      )}
                      <Text fontSize="xs" color="gray.400" whiteSpace="nowrap">
                        {formatTimeAgo(n.created_at)}
                      </Text>
                    </HStack>
                  </HStack>
                ))}
              </VStack>
            )}
          </Card.Body>
        </Card.Root>

        {/* Pagination */}
        {totalPages > 1 && (
          <HStack justify="space-between" align="center">
            <Text fontSize="sm" color="gray.500">
              {t('admin.main.notifications.showingRange', { start: ((page - 1) * pageSize) + 1, end: Math.min(page * pageSize, total), total })}
            </Text>
            <HStack gap={2}>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage(1)}
                disabled={page === 1}
              >
                {t('admin.main.pagination.first')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                {t('admin.main.pagination.previous')}
              </Button>
              <Text fontSize="sm" fontWeight="medium" px={2}>
                {t('admin.main.pagination.pageOf', { page, totalPages })}
              </Text>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                {t('admin.main.pagination.next')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages}
              >
                {t('admin.main.pagination.last')}
              </Button>
            </HStack>
          </HStack>
        )}
      </VStack>
    </Box>
  );
};


// --- DOCUMENTATION ---
const DocumentationSection = () => {
  const { t } = useTranslation();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Docs are served through the same origin (nginx proxies /api/ to the backend).
  // Port 8000 is bound to localhost only and is NOT publicly reachable.
  const apiDocsUrl = `${window.location.origin}/api/docs`;

  return (
    <Box p={6}>
      <VStack gap={6} align="stretch">
        <VStack align="start" gap={1}>
          <Heading size="lg">{t('admin.main.documentation.title')}</Heading>
          <Text color="gray.600" fontSize="sm">
            {t('admin.main.documentation.subtitle')}
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
          {/* {t('admin.main.config.apiDocumentation')} */}
          <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
            <Card.Header bg="purple.50" borderBottom="1px" borderColor={borderColor}>
              <HStack>
                <FiKey />
                <Heading size="md">{t('admin.main.config.apiDocumentation')}</Heading>
              </HStack>
            </Card.Header>
            <Card.Body>
              <VStack gap={3} align="stretch">
                <Text fontSize="sm" color="gray.600">
                  {t('admin.main.documentation.apiDescription')}
                </Text>
                <Button
                  colorScheme="purple"
                  size="sm"
                  onClick={() => window.open(apiDocsUrl, '_blank')}
                >
                  <FiExternalLink />
                  {t('admin.main.documentation.openSwagger')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(apiDocsUrl.replace('/docs', '/redoc'), '_blank')}
                >
                  <FiExternalLink />
                  {t('admin.main.documentation.openRedoc')}
                </Button>
              </VStack>
            </Card.Body>
          </Card.Root>

          {/* Admin Guides */}
          <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
            <Card.Header bg="blue.50" borderBottom="1px" borderColor={borderColor}>
              <HStack>
                <FiBookOpen />
                <Heading size="md">{t('admin.main.documentation.adminGuides')}</Heading>
              </HStack>
            </Card.Header>
            <Card.Body>
              <VStack gap={3} align="stretch">
                <Text fontSize="sm" color="gray.600">
                  {t('admin.main.documentation.adminGuidesDescription')}
                </Text>
                <VStack align="stretch" gap={2}>
                  <HStack px={3} py={2} bg={useColorModeValue('gray.50', 'gray.700')} borderRadius="md">
                    <FiBookOpen />
                    <VStack align="start" gap={0} flex={1}>
                      <Text fontSize="sm" fontWeight="medium">{t('admin.main.documentation.gettingStarted')}</Text>
                      <Text fontSize="xs" color="gray.500">{t('admin.main.documentation.gettingStartedDescription')}</Text>
                    </VStack>
                  </HStack>
                  <HStack px={3} py={2} bg={useColorModeValue('gray.50', 'gray.700')} borderRadius="md">
                    <FiUsers />
                    <VStack align="start" gap={0} flex={1}>
                      <Text fontSize="sm" fontWeight="medium">{t('admin.main.sidebar.items.users.label')}</Text>
                      <Text fontSize="xs" color="gray.500">{t('admin.main.documentation.userManagementDescription')}</Text>
                    </VStack>
                  </HStack>
                  <HStack px={3} py={2} bg={useColorModeValue('gray.50', 'gray.700')} borderRadius="md">
                    <FiShield />
                    <VStack align="start" gap={0} flex={1}>
                      <Text fontSize="sm" fontWeight="medium">{t('admin.main.documentation.complianceStandards')}</Text>
                      <Text fontSize="xs" color="gray.500">{t('admin.main.documentation.complianceStandardsDescription')}</Text>
                    </VStack>
                  </HStack>
                  <HStack px={3} py={2} bg={useColorModeValue('gray.50', 'gray.700')} borderRadius="md">
                    <FiSettings />
                    <VStack align="start" gap={0} flex={1}>
                      <Text fontSize="sm" fontWeight="medium">{t('admin.main.documentation.securityConfiguration')}</Text>
                      <Text fontSize="xs" color="gray.500">{t('admin.main.documentation.securityConfigurationDescription')}</Text>
                    </VStack>
                  </HStack>
                </VStack>
              </VStack>
            </Card.Body>
          </Card.Root>
        </SimpleGrid>

        {/* Quick Links */}
        <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
          <Card.Header>
            <Heading size="md">{t('admin.main.documentation.quickLinks')}</Heading>
          </Card.Header>
          <Card.Body>
            <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(apiDocsUrl, '_blank')}
              >
                <FiKey />
                {t('admin.main.documentation.swaggerUi')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open('https://www.chatwoot.com/docs', '_blank')}
              >
                <FiMessageCircle />
                {t('admin.main.documentation.chatwootDocs')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open('https://github.com', '_blank')}
              >
                <FiExternalLink />
                {t('admin.main.documentation.githubRepository')}
              </Button>
            </SimpleGrid>
          </Card.Body>
        </Card.Root>
      </VStack>
    </Box>
  );
};


const AdminPage: React.FC = () => {
  const { t } = useTranslation();
  const [section, setSection] = useState('dashboard');
  const disclosure = useDisclosure();
  const { open: isOpen, onOpen, onClose } = disclosure;
  
  const bg = useColorModeValue('gray.50', 'gray.900');

  // Render content based on current section
  const renderContent = () => {
    switch (section) {
      case 'dashboard':
        return <AdminDashboard setSection={setSection} />;
      case 'users':
        return <UserManagement />;
      case 'standards':
        return <StandardsManagement />;
      case 'content':
        return <ContentReviewsManagement />;
      case 'billing':
        return <SubscriptionManagement />;
      case 'subscription_plans':
        return <SubscriptionPlanManagement />;
      case 'analytics':
        return <AnalyticsReporting />;
      case 'security':
        return <SecurityComplianceSection />;
      case 'system':
        return <SystemStatus />;
      case 'configuration':
        return <ConfigurationManagement />;
      case 'backup':
        return <BackupRecoverySection />;
      case 'support':
        return <ChatwootSupportSection />;
      case 'notifications':
        return <NotificationsSection />;
      case 'documentation':
        return <DocumentationSection />;
      case 'subscription_plan_management':
        return <SubscriptionPlanManagement />;
      default:
        return <AdminDashboard setSection={setSection} />;
    }
  };

  return (
    <Flex minH="100vh">
      {/* Mobile Menu Button */}
      <Box
        position="fixed"
        top={4}
        left={4}
        zIndex={1000}
        display={{ base: 'block', lg: 'none' }}
      >
        <IconButton
          aria-label={t('admin.main.openAdminMenu')}
          onClick={onOpen}
          colorScheme="brand"
          size="lg"
          borderRadius="full"
          shadow="lg"
        >
          <FiMenu />
        </IconButton>
      </Box>

      {/* Desktop Sidebar */}
      <Box display={{ base: 'none', lg: 'block' }}>
        <AdminSidebar currentSection={section} onSectionChange={setSection} />
      </Box>

      {/* Mobile Drawer */}
      <Drawer.Root open={isOpen} placement="start" onOpenChange={(details) => details.open ? onOpen() : onClose()} size="full">
        <DrawerBackdrop />
        <DrawerContent>
          <DrawerCloseTrigger />
          <DrawerHeader>{t('admin.main.adminPortal')}</DrawerHeader>
          <DrawerBody p={0}>
            <AdminSidebar 
              currentSection={section} 
              onSectionChange={(newSection) => {
                setSection(newSection);
                onClose();
              }} 
            />
          </DrawerBody>
        </DrawerContent>
      </Drawer.Root>

      {/* Main Content */}
      <Box 
        flex="1" 
        p={{ base: 4, md: 6, lg: 8 }} 
        bg={bg} 
        minH="100vh" 
        overflowX="auto"
        ml={{ base: 0, lg: 0 }}
        pt={{ base: 20, lg: 8 }}
      >
        {renderContent()}
      </Box>
    </Flex>
  );
};

export default AdminPage;
